import React, { ReactElement, useState, Suspense, useEffect, useRef } from 'react';
import { DI, DIProps } from '../../../../Core';
import { Page, Badge, FooterHelp, Icon, Link, ContextualSaveBar, Modal, Loading, Banner, SkeletonBodyText, Text, InlineStack, BlockStack, Layout, Checkbox } from '@shopify/polaris';
import { Provider } from '../../../../Actions/NecessaryFun';
import TemplateSkeleton from '../Template/Components/skeleton/TemplateSkeleton'
import { RenderHeadersAction } from '../../Panel';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CircleInformationMajor } from '@shopify/polaris-icons';
import useContextualSaveBar, { isAdminLoggedIn } from '../../Panel/index'
import { validateAndUpdateMetaAttributeExistance } from '../../Editproduct/utils/utils';
import _ from 'lodash';
import { HandleJSONFeedSaveT, JSONFeedBackendDataI } from '../../JSONFeed/interface';
import { CircleTickMinor } from '@shopify/polaris-icons';
import LoadingDocTemplate from '../../../../Asests/Images/loading-doc-template.gif';
import Tick from '../../../../Asests/Images/Tick.gif';
import TopTabs from './TopTabs';
import { AttributeMapBanner } from '../../Editproduct/utils/CustomHeader';
import SettingsTour from './SettingsTour';
import { useTour } from '../../../Other/tour';
import { CategorySkeleton, FilterSkelton, FullfillmentSkeleton, InventorySkeleton, jsonAttributesSkeleton, PriceSkeleton, ProductSkeleton, SmallSkeleton } from '../Template/Components/skeleton/Skeleton';
import { useTranslation } from 'react-i18next';
import { validatePriceEntries } from '../../Editproduct/components/BusinessPrice/BusinessPriceN';

interface Props extends DIProps {
    name?: string;
    syncNecessaryInfo: () => void;
    syncConnectorInfo: (props: any, shop_url?: string | null, updateCurrentShop?: boolean, updateStepStatus?: React.Dispatch<React.SetStateAction<string>>) => void;
    syncProfileInfo: () => void;
    loginStatus: () => void;
    syncServices: () => void;
}

const MemoizedCategorySettingsAndAttributes: any = React.lazy(() => import('../Template/Components/CategorySettingsAndAttributes/CategorySettingsAndAttributes'));
const MemoizedTemplateName: any = React.lazy(() => import('../Template/Components/TemplateName'));
const MemoizedFulfillmentType: any = React.lazy(() => import('../Template/Components/FulfillmentType'));
const MemoizedInventorySettings: any = React.lazy(() => import('../Template/Components/InventorySettings'));
const MemoizedPriceSettings: any = React.lazy(() => import('../Template/Components/PriceSettings'));
const MemoizedProductSettings: any = React.lazy(() => import('../Template/Components/ProducSettings'));
const MemoizedBulkAttributeEdit: any = React.lazy(() => import('../Template/Components/BulkAttributeEdit/BulkAttributeEdit'));
const FilterProducts = React.lazy(() => import('../Template/Components/FilterProducts/FilterProducts'));
const JSONFeedAttributes = React.lazy(() => import('../../JSONFeed/JSONFeedAttributes'));

const errorsObj = {
    max_inventory_level: false,
    template_name: false,
    amazon_seller_id: false,
    category_settings1: false,
    category_settings: {
        primary_category: false,
        sub_category: false,
        browser_node_id: false,
        attributes_mapping: {
            required_attribute: [],
            optional_attribute: [],
        },
    },
    filter_products: {
        prepareQuery: {
            query: false,
        },
        queryArray: [[{ attribute: false, condition: false, value: false }]],
        productsFiltered: {
            productsCount: false,
        },
        manual_product_ids: false,
        none_selected: false,
    },
    inventory_settings: {
        effective_inventory: false,
        fixed_inventory: false,
        reserved_inventory: false,
        threshold_inventory: false,
        warehouses: false,
        inventory_fulfillment_latency: false
    },
    pricing_settings: {
        settings_enabled: false,
        settings_selected: {
            sale_price: false,
            business_price: false,
            minimum_price: false,
        },
        standard_price: {
            attribute: false,
            value: false,
        },
        sale_price: {
            attribute: false,
            value: false,
            start_date: false,
            end_date: false,
        },
        business_price: {
            attribute: false,
            value: false,
            // price_roundoff: false,
            "fixed_discount": [
                {
                    "quantity": false,
                    "price": false,
                    "touched": {
                        "quantity": false,
                        "price": false,
                    }
                }
            ],
            "percentage_discount": [
                {
                    "quantity": false,
                    "price": false,
                    "touched": {
                        "quantity": false,
                        "price": false,
                    }
                }
            ],
        },
        minimum_price: {
            attribute: false,
            value: false,
        },
    },
    sync_status_with: false
}
let defaultPriceSettings: any = {
    "data_type": "price_settings",
    "settings_enabled": true,
    "settings_selected": {
        "sale_price": false,
        "business_price": false,
        "minimum_price": false
    },
    "standard_price": {
        "attribute": "default",
        "value": "",
        "price_roundoff_value": '',
    },
    "sale_price": {
        "attribute": "default",
        "value": "",
        "price_roundoff_value": '',
        "start_date": "",
        "end_date": ""
    },
    "business_price": {
        "attribute": "",
        "value": "",
        "price_roundoff_value": '',
        "fixed_discount": [
            {
                "quantity": "",
                "price": ""
            }
        ],
        "percentage_discount": [
            {
                "quantity": "",
                "price": ""
            }
        ],
        "quantity_type": "",
    },
    "minimum_price": {
        "attribute": "default",
        "value": "",
        "price_roundoff_value": '',
    }
};
let defaultProductSettings: any = {
    "data_type": "product_settings",
    "settings_enabled": true,
    "images": false,
    "variant_title": true,
    "product_details": false,
    "selected_attributes": [],
    "sync_status_with": {
        "sku": false,
        "barcode": false
    },
};
let defaultInventorySettings: any = {
    "data_type": "inventory_settings",
    "inventory_settings": true,
    "continue_selling": {
        "enabled": true,
        "value": "999"
    },
    "effective_inventory": {
        "enabled": false,
        "type": "none",
        "value": ""
    },
    "inactivate_product": false,
    "warehouses": [],
    "inventory_fulfillment_latency": 2
};
let defaultCategoryAndAttributesSettings: any = {
    category_settings: {
        "primary_category": "",
        "sub_category": "",
        "browser_node_id": "",
        "barcode_exemption": false,
        "displayPath": [],
        "parentNodes": [],
        "type": ""
    },
    attributes_mapping: {
        "required_attribute": [],
        "optional_attribute": [],
    }
}
let defaultFilterSettings: any = {
    "query": "",
    "overWriteExistingProducts": false,
    "manual_product_ids": [],
    "queryArray": [[{ attribute: "", condition: "", value: "" }]],
    "manual_search": "",
    "type": "",
    'filter_type': ''
}

let defaultRoundOffUi = {
    "standard_price": {
        "price_roundoff_value": '',
    },
    "sale_price": {
        "price_roundoff_value": '',
    },
    "business_price": {
        "price_roundoff_value": '',
    },
    "minimum_price": {
        "price_roundoff_value": '',
    }
}

function Template(props: Props): ReactElement {
    const { t } = useTranslation();
    const [showBusinessPrice, setShowBusinessPrice] = useState<boolean>(false);
    const [savedShowBusinessPrice, setSavedShowBusinessPrice] = useState<boolean>(false);
    const { isOpen, setTourLoading, setCurrentStep, setIsOpen } = useTour()
    const [manualProductInAnotherTemplate, setManualProductInAnotherTemplate] = useState<boolean>(false);
    let [intervalId, setintervalId] = useState<any>('');
    const navigate = useNavigate();
    const { showSaveBar, hideSaveBar } = useContextualSaveBar(props);
    const [roundOffUi, setRoundOffUi] = useState<any>(JSON.parse(JSON.stringify(defaultRoundOffUi)));
    const [loadingProgress, setLoadingProgress] = useState<number>(0);
    const [valueMappingData, setValueMappingData] = useState<any>({});
    const [valueMappingModal, setValueMappingModal] = useState<boolean>(false);
    const [loadingModal, setLoadingModal] = useState<boolean>(false);
    const [isClonedTemplate, setIsClonedTemplate] = useState<boolean>(false);
    const [filterAttributesLoader, setFilterAttributesLoader] = useState<boolean>(false);
    const [productsAssigned, setProductsAssigned] = useState<number>(0);
    const [updateInprogress, setUpdateInprogress] = useState<boolean>(false);
    const [backendQuery, setBackendQuery] = useState<string>('');
    const [disableBarcodeExemption, setDisableBarcodeExemption] = useState<boolean>(false);
    const [showSkeleton, setShowSkeleton] = useState<boolean>(true);
    const [saveloader, setSaveLoader] = useState<boolean | undefined>(false);
    const [wareHouseOptions, setwareHouseOptions] = useState<any>([]);
    const [templateId, setTemplateId] = useState<string | null>(null);
    const [valuemappingLoader, setValueMappingLoader] = useState<boolean>(false);
    const [onClickResetFilters, setOnClickResetFilters] = useState<boolean>(false);
    const [isAnotherProfileProductExists, setIsAnotherProfileProductExists] = useState<boolean>(false);
    const [selectedProductCount, setSelectedProductCount] = useState<number>(0);
    const [savedProfileData, setSavedProfileData] = useState<any>({
        "fulfillment_type": "FBM",
        "name": "",
        "query": "",
        "queryArray": [],
        "filter_type": "",
        "manual_product_ids": [],
        "category_id": {
            "primary_category": "",
            "sub_category": "",
            "browser_node_id": "",
            "barcode_exemption": false,
            "displayPath": [],
            "parentNodes": [],
            "type": ""
        },
        // "initialAttributes": 'null',
        // "currentAttributes": 'null',
        "overWriteExistingProducts": false,
        "data": [
            {
                "data_type": "pricing_settings",
                "settings_enabled": true,
                "settings_selected": {
                    "sale_price": false,
                    "business_price": false,
                    "minimum_price": false
                },
                "standard_price": {
                    "attribute": "default",
                    "value": ""
                },
                "sale_price": {
                    "attribute": "default",
                    "value": "",
                    "start_date": "",
                    "end_date": ""
                },
                "business_price": {
                    "attribute": "default",
                    "value": "",
                    "price_roundoff_value": '',
                    "fixed_discount": [
                        {
                            "quantity": "",
                            "price": ""
                        }
                    ],
                    "percentage_discount": [
                        {
                            "quantity": "",
                            "price": ""
                        }
                    ],
                    "quantity_type": "fixed",
                },
                "minimum_price": {
                    "attribute": "default",
                    "value": ""
                }
            },
            {
                "data_type": "inventory_settings",
                "inventory_settings": true,
                "continue_selling": {
                    "enabled": true,
                    "value": "999"
                },
                "effective_inventory": {
                    "enabled": false,
                    "type": "none",
                    "value": ""
                },
                "inactivate_product": false,
                "warehouses": [],
                "inventory_fulfillment_latency": '2'
            },
            {
                "data_type": "product_settings",
                "settings_enabled": true,
                "images": false,
                "product_details": false,
                "selected_attributes": [],
                "sync_status_with": {
                    "sku": false,
                    "barcode": false
                }
            },
        ],
        "attributes_mapping": {
            "required_attribute": [],
            "optional_attribute": [],
        },
    });

    const [attributesLoader, setattributesLoader] = useState<boolean>(false);
    const [shopifyAttributesLoader, setShopifyAttributesLoader] = useState<boolean>(true);

    const [shopifyAttributes, setShopifyAttributes] = useState<any>([]);

    const [templateName, setTemplateName] = useState<string>('');
    const [fulfillmentType, setFulfillmentType] = useState<string>('FBM');

    const [errors, setErrors] = useState<any>(JSON.parse(JSON.stringify(errorsObj)));

    const [defaultAttributes, setDefaultAttributes] = useState<any>({});
    const [discardBtnClicked, setDiscardBtnClicked] = useState<boolean>(false);

    const JSONFeedRef = useRef<HandleJSONFeedSaveT>(null);
    const [JSONFeedAttributesData, setJSONFeedDataAttributes] = useState<any>()
    const [savedJSONFeedAttributesData, setSavedJSONFeedDataAttributes] = useState<any>()
    const [currentData, setCurrentData] = useState<any>({});
    const [renderJSONFeed, setRenderJSONFeed] = useState<boolean>(false)
    const [isNewTemplate, setIsNewTemplate] = useState<boolean>(false);
    const [allProductTypes, setAllProductTypes] = useState<string[]>([]);
    const [jsonDataOnUI, setJsonDataOnUi] = useState<any>({})
    const [initProductType, setInitProductType] = useState<any>('');

    let [contextualSaveBarData, setContextualSaveBarData] = useState<any>({});
    const [showContextualSaveBar, setShowContextualSaveBar] = useState<boolean>(false);
    const handleContextualSaveBar = (key: string, value: boolean) => {
        const tempContextualSaveBar = JSON.parse(JSON.stringify(contextualSaveBarData))
        tempContextualSaveBar[key] = value;
        contextualSaveBarData = {
            ...contextualSaveBarData,
            [key]: value
        }
        setContextualSaveBarData({ ...contextualSaveBarData })
    }

    const [validatorErrAfterSave, setValidatorErrorAfterSave] = useState<any>([])
    const [validationModal, setvalidationModal] = useState({
        open: false,
        actionLoader: false,
        validationRequired: false
    })

    const deleteInContextualSaveBar = (key: string) => {
        delete contextualSaveBarData[key];
        setContextualSaveBarData({ ...contextualSaveBarData })
    }

    const resetCat = () => {
        setDefaultAttributes({ ...savedProfileData.attributes_mapping });
        onAttributesSettingsChange("required_attribute", [...savedProfileData.attributes_mapping.required_attribute]);
        onAttributesSettingsChange("optional_attribute", [...savedProfileData.attributes_mapping.optional_attribute]);
        contextualSaveBarData = { ...contextualSaveBarData }
        delete contextualSaveBarData.categorySettings
        Object.keys(contextualSaveBarData).forEach((key: string) => {
            if (key.includes('optionalAttribute_') || key.includes('requiredAttribute_'))
                delete contextualSaveBarData[key]
        })
        setContextualSaveBarData({ ...contextualSaveBarData })
    }

    useEffect(() => {
        let show = false;
        Object.values(contextualSaveBarData).map(val => {
            if (val) show = true
        })
        setShowContextualSaveBar(show)
    }, [contextualSaveBarData])

    /*
    * Compare two objects by reducing an array of keys in obj1, having the
    * keys in obj2 as the intial value of the result. Key points:
    *
    * - All keys of obj2 are initially in the result.
 *
 * - If the loop finds a key (from obj1, remember) not in obj2, it adds
 *   it to the result.
 *
 * - If the loop finds a key that are both in obj1 and obj2, it compares
 *   the value. If it's the same value, the key is removed from the result.
 */
    function getObjectDiff(newObj: any, oldObj: any) {
        const diff = Object.keys(newObj).reduce((result, key) => {
            if (!oldObj.hasOwnProperty(key)) {
                result.push(key);
            } else if (_.isEqual(newObj[key], oldObj[key])) {
                const resultKeyIndex = result.indexOf(key);
                result.splice(resultKeyIndex, 1);
            }
            return result;
        }, Object.keys(oldObj));

        return diff;
    }

    const [productSettings, setProductSettings] = useState<any>({ ...defaultProductSettings });
    const onProductSettingsChange = (key: string, subKey: string, value: boolean) => {
        let tempSettings: any = JSON.parse(JSON.stringify(productSettings));
        if (subKey != "") {
            tempSettings[key][subKey] = value;
        }
        else {
            tempSettings[key] = value;
            if (key != "variant_title") {
                if (value) {
                    tempSettings?.['selected_attributes']?.push(key);
                }
                else {
                    tempSettings?.['selected_attributes']?.splice(tempSettings['selected_attributes']?.indexOf(key), 1);
                }
            }
        }

        setProductSettings(JSON.parse(JSON.stringify(tempSettings)));
        enableDisableContextualSaveBarForProductSettings(templateId == "default" ? "product" : "product_settings", JSON.parse(JSON.stringify(tempSettings)))
    }
    const enableDisableContextualSaveBarForProductSettings = (key: string, newValue: any) => {
        let oldValue = JSON.parse(JSON.stringify(defaultProductSettings));
        if (templateId == 'default') {
            Object.values(savedProfileData).map((obj: any) => {
                if (obj['key'] == key && obj?.['value'] && Object.keys(obj['value'])?.length > 0) {
                    if (obj?.value?.hasOwnProperty('settings_enabled')) oldValue['settings_enabled'] = obj?.['value']?.settings_enabled
                    if (obj?.value?.hasOwnProperty('images')) oldValue['images'] = obj?.['value']?.images
                    if (obj?.value?.hasOwnProperty('product_details')) oldValue['product_details'] = obj?.['value']?.product_details
                    if (obj?.value?.hasOwnProperty('selected_attributes')) oldValue['selected_attributes'] = obj?.['value']?.selected_attributes

                    if (obj?.value?.hasOwnProperty('sync_status_with') && obj?.value?.sync_status_with) {
                        if (obj?.value?.sync_status_with.constructor.name === "Object" && Object.keys(obj?.value?.sync_status_with).length > 0) {
                            oldValue['sync_status_with'] = obj?.['value']?.sync_status_with
                        }
                        else if (Array.isArray(obj?.value?.sync_status_with) && obj?.value?.sync_status_with?.length > 0) {
                            obj?.value?.sync_status_with?.map((key: any) => {
                                if (key == "sku") {
                                    oldValue['sync_status_with']['sku'] = true;
                                }
                                else if (key == "barcode") {
                                    oldValue['sync_status_with']['barcode'] = true;
                                }
                            })
                        }
                    }
                    else {
                        oldValue['sync_status_with'] = {
                            "sku": true,
                            "barcode": false
                        }
                    }
                }
            });
        }
        else {
            savedProfileData?.['data']?.map((obj: any) => {
                if (obj['data_type'] == key) {
                    oldValue = { ...defaultProductSettings, ...obj['data'] };
                }
            });
        }
        if ((newValue?.selected_attributes?.length === oldValue?.selected_attributes?.length) && compareStringValidator(oldValue?.selected_attributes, newValue?.selected_attributes)) {
            newValue['selected_attributes'] = oldValue?.selected_attributes;
        }
        if (JSON.stringify(newValue) != JSON.stringify(oldValue)) handleContextualSaveBar("productSettings", true);
        else handleContextualSaveBar("productSettings", false)
    }

    const [inventorySettings, setInventorySettings] = useState<any>({ ...defaultInventorySettings });

    const compareInventorySettings = (settings: any, savedSettings: any): { [key: string]: boolean } => {
        const contextualObj: { [key: string]: boolean } = {
            inventory_settings_switcher: false,
            continue_selling_switcher: false,
            continue_selling: false,
            effective_inventory_switcher: false,
            effective_inventory_type: false,
            effective_inventory: false,
            inactivate_product_switcher: false,
            warehouses: false,
        };
        contextualObj.inventory_settings_switcher = settings.inventory_settings !== savedSettings.inventory_settings;
        // contextualObj.inventorySettings_inventoryFulfillmentLatency = settings.inventory_fulfillment_latency !== savedSettings.inventory_fulfillment_latency
        // IF INVENTORY SETTINGS ARE ENABLED THEN COMPARE REST OF SECTIONS
        if (settings?.inventory_settings) {
            // COMPARE IF SETINGS OF INVENTORY ARE ENABLED
            contextualObj.continue_selling_switcher = settings.continue_selling.enabled !== savedSettings.continue_selling.enabled;
            contextualObj.continue_selling = settings.continue_selling.value !== savedSettings.continue_selling.value;
            contextualObj.effective_inventory_switcher = settings.effective_inventory.enabled !== savedSettings.effective_inventory.enabled;
            contextualObj.effective_inventory_type = settings.effective_inventory.type !== savedSettings.effective_inventory.type;
            contextualObj.effective_inventory = settings.effective_inventory.value !== savedSettings.effective_inventory.value;
            contextualObj.inactivate_product_switcher = settings.inactivate_product !== savedSettings.inactivate_product
            contextualObj.warehouses = JSON.stringify(settings.warehouses) !== JSON.stringify(savedSettings.warehouses)

            // COMPARE MAX INVENTORY LEVEL IF IT IS ENABLED
            if (settings.enable_max_inventory_level)
                contextualObj.inventorySettings_maxInventoryLevel = settings.max_inventory_level !== savedSettings.max_inventory_level

            // COMPARE FIXED INVENTORY IF IT IS ENABLED
            if (settings.settings_selected.fixed_inventory)
                contextualObj.inventorySettings_fixedInventory = settings.fixed_inventory !== savedSettings.fixed_inventory

            // COMPARE RESERVED INVENTORY IF IT IS ENABLED
            if (settings.settings_selected.customize_inventory)
                contextualObj.inventorySettings_customizeInventory = settings.customize_inventory.value !== savedSettings.customize_inventory.value

            // COMPARE THRESHOLD INVENTORY IF IT IS ENABLED
            if (settings.settings_selected.threshold_inventory)
                contextualObj.inventorySettings_thresholdInventory = settings.threshold_inventory !== savedSettings.threshold_inventory
        }
        return JSON.parse(JSON.stringify(contextualObj))
    }
    const enableDisableContextualSaveBarForInventorySettings = (key: string, newValue: any) => {

        let oldValue = { ...defaultInventorySettings };
        if (templateId) {
            if (templateId == 'default') {
                Object.values(savedProfileData).map((obj: any) => {
                    if (obj['key'] == key && obj?.['value'] && Object.keys(obj['value'])?.length > 0) {
                        if (obj?.value?.hasOwnProperty('data_type')) oldValue['data_type'] = obj?.['value']?.data_type
                        if (obj?.value?.hasOwnProperty('settings_enabled')) oldValue['settings_enabled'] = obj?.['value']?.settings_enabled
                        if (obj?.value?.hasOwnProperty('settings_selected')) oldValue['settings_selected'] = obj?.['value']?.settings_selected
                        if (obj?.value?.hasOwnProperty('enable_max_inventory_level')) oldValue['enable_max_inventory_level'] = obj?.['value']?.enable_max_inventory_level
                        if (obj?.value?.hasOwnProperty('max_inventory_level')) oldValue['max_inventory_level'] = obj?.['value']?.max_inventory_level
                        if (obj?.value?.hasOwnProperty('fixed_inventory')) oldValue['fixed_inventory'] = obj?.['value']?.fixed_inventory
                        if (obj?.value?.hasOwnProperty('threshold_inventory')) oldValue['threshold_inventory'] = obj?.['value']?.threshold_inventory
                        if (obj?.value?.hasOwnProperty('reserved_inventory')) oldValue['reserved_inventory'] = obj?.['value']?.reserved_inventory
                        if (obj?.value?.hasOwnProperty('warehouses')) oldValue['warehouses'] = obj?.['value']?.warehouses
                        if (obj?.value?.hasOwnProperty('inventory_fulfillment_latency')) oldValue['inventory_fulfillment_latency'] = obj?.['value']?.inventory_fulfillment_latency
                        if (obj?.value?.hasOwnProperty('customize_inventory')) oldValue['customize_inventory'] = obj?.['value']?.customize_inventory
                    }
                });
            }
            else {
                savedProfileData?.['data']?.map((obj: any) => {
                    if (obj['data_type'] == key) {
                        oldValue = obj['data'];
                    }
                });
            }
        } else { //for new template
            oldValue = JSON.parse(JSON.stringify(savedProfileData?.inventory_settings))
        }
        if ((oldValue?.warehouses?.length === newValue?.warehouses?.length) && compareStringValidator(oldValue?.warehouses, newValue?.warehouses)) {
            newValue['warehouses'] = oldValue?.warehouses;
        }
        setContextualSaveBarData((a: any) => ({ ...a, ...compareInventorySettings(newValue, oldValue) }))
    }
    const formatInventorySettings = (initSettings: any) => {
        let tempInventorySettings: any = { ...initSettings }
        if (!tempInventorySettings.inventory_settings) {
            tempInventorySettings = {
                ...initSettings,
                "data_type": "inventory_settings",
                "inventory_settings": false,
                "continue_selling": {
                    "enabled": true,
                    "value": "999"
                },
                "effective_inventory": {
                    "enabled": false,
                    "type": "none",
                    "value": ""
                },
                "inactivate_product": false,
                "inventory_fulfillment_latency": tempInventorySettings.inventory_fulfillment_latency,
            };

        } else {
            if (!tempInventorySettings.effective_inventory.enabled) {
                tempInventorySettings.effective_inventory.type = "none"
                tempInventorySettings.effective_inventory.value = ""
            }
        }
        return tempInventorySettings
    }

    function compareStringValidator(oldValue: any, newValue: any) {
        let flag: boolean = true;
        if (oldValue?.length === newValue?.length) {
            oldValue?.map((event: any) => {
                if (flag) {
                    flag = newValue.includes(event);
                }
            })
            return flag
        } else {
            return !flag;
        }
    }

    const resetInventorySettingsToDefault = (key: string) => {
        let tempInventorySettings: any = {};
        if (key == "customize_inventory") {
            tempInventorySettings = {
                ...inventorySettings,
                [key]: { ...inventorySettings[key], value: "" },
                "settings_selected": {
                    ...inventorySettings.settings_selected,
                    [key]: false
                },
            };
        }
        else if (key == "threshold_inventory") {
            tempInventorySettings = {
                ...inventorySettings,
                [key]: "",
                "settings_selected": {
                    ...inventorySettings.settings_selected,
                    [key]: false
                },
            };
        }
        else if (key == "max_inventory_level") {
            tempInventorySettings = {
                ...inventorySettings,
                enable_max_inventory_level: false,
                [key]: "",
            };
        }
        else if (key == "fixed_inventory") {
            tempInventorySettings = {
                ...inventorySettings,
                [key]: "",
                "settings_selected": {
                    ...inventorySettings.settings_selected,
                    [key]: false
                },
            };
        }
        setInventorySettings({ ...tempInventorySettings });
        let inventorySettingsObj = savedProfileData?.data?.filter((obj: any) => obj?.data_type == "inventory_settings")
        if (JSON.stringify(inventorySettingsObj?.[0]?.data) != JSON.stringify(tempInventorySettings)) handleContextualSaveBar("inventorySettings", true);
        else handleContextualSaveBar("inventorySettings", false);
    }

    const onInventorySettingsChange = (key: string, subKey: string, value: any, switcher: boolean) => {
        let tempInventorySettings: any = JSON.parse(JSON.stringify(inventorySettings));
        if (key == "warehouses") {
            tempInventorySettings['warehouses'] = value;
            if (value.length === 1) {
                wareHouseOptions.map(
                    (warehouse: any) => {
                        if (warehouse.value === value[0]) {
                            warehouse.disabled = true;
                        }
                    }
                );
            } else if (value.length > 1) {
                wareHouseOptions.filter((option: any) => option.deactivated == false).map(
                    (warehouse: any) => {
                        warehouse.disabled = false;
                    }
                );
            }
            setwareHouseOptions([...wareHouseOptions])
        }
        else {
            if (subKey) {
                tempInventorySettings[key][subKey] = value;
            }
            else {
                tempInventorySettings[key] = value
            }
        }
        setInventorySettings(JSON.parse(JSON.stringify(tempInventorySettings)));
    }

    const checkForStandardPrice = (newStandardPrice: any, oldStandardPrice: any): boolean => {
        if (newStandardPrice?.attribute === 'default') return newStandardPrice?.attribute !== oldStandardPrice?.attribute
        return newStandardPrice?.attribute !== oldStandardPrice?.attribute || newStandardPrice?.value !== oldStandardPrice?.value
    }

    const [priceSettings, setPriceSettings] = useState<any>({ ...JSON.parse(JSON.stringify(defaultPriceSettings)) });
    const comparePriceSettings = (settings: any, savedSettings: any): { [key: string]: boolean } => {
        let contextualObj: { [key: string]: boolean } = {
            priceSettings_settingsEnabled: false,
            priceSettings_salePrice_settingsEnabled: false,
            priceSettings_businessPrice_settingsEnabled: false,
            priceSettings_minimumPrice_settingsEnabled: false,
            priceSettings_standardPrice_attribute: false,
            priceSettings_standardPrice_value: false,
            priceSettings_standardPrice_roundOffValue: false,
            priceSettings_businessPrice_attribute: false,
            priceSettings_businessPrice_value: false,
            priceSettings_businessPrice_roundOffValue: false,
            priceSettings_minimumPrice_attribute: false,
            priceSettings_minimumPrice_value: false,
            priceSettings_minimumPrice_roundOffValue: false,
            priceSettings_salePrice_attribute: false,
            priceSettings_salePrice_value: false,
            priceSettings_salePrice_roundOffValue: false,
            priceSettings_salePrice_startDate: false,
            priceSettings_salePrice_endDate: false,
        };
        contextualObj.priceSettings_settingsEnabled = settings.settings_enabled !== savedSettings.settings_enabled
        // IF PRICE SETTINGS ARE ENABLED THEN COMPARE REST OF SECTIONS
        if (settings?.settings_enabled) {
            // COMPARE IF SALE,BUSINESS AND MINIMUM PRICE ARE ENABLED
            contextualObj.priceSettings_salePrice_settingsEnabled = settings.settings_selected.sale_price !== savedSettings.settings_selected.sale_price
            contextualObj.priceSettings_businessPrice_settingsEnabled = settings.settings_selected.business_price !== savedSettings.settings_selected.business_price
            contextualObj.priceSettings_minimumPrice_settingsEnabled = settings.settings_selected.minimum_price !== savedSettings.settings_selected.minimum_price
            // COMPARE ATTRIBUTE OF STANDARD PRICE
            contextualObj.priceSettings_standardPrice_attribute = settings.standard_price.attribute !== savedSettings.standard_price.attribute
            // COMPARE ROUND OFF VALUE FOR STANDARD PRICE
            contextualObj.priceSettings_standardPrice_roundOffValue = JSON.stringify(settings.standard_price.price_roundoff_value) !== JSON.stringify(savedSettings.standard_price.price_roundoff_value)
            // ONLY COMPARE VALUE OF STANDARD PRICE IF ITS ATTRIBUTE IS NOT DEFAULT
            if (settings.standard_price.attribute !== 'default')
                contextualObj.priceSettings_standardPrice_value = settings.standard_price.value !== savedSettings.standard_price.value
            // COMPARE ATTRIBUTE AND VALUE OF BUSINESS PRICE IF ITS ENABLED
            if (settings.settings_selected.business_price) {
                // COMPARE ATTRIBUTE OF BUSINESS PRICE
                contextualObj.priceSettings_businessPrice_attribute = settings.business_price.attribute !== savedSettings.business_price.attribute
                // COMPARE ROUND OFF VALUE FOR BUSINESS PRICE
                contextualObj.priceSettings_businessPrice_roundOffValue = JSON.stringify(settings.business_price.price_roundoff_value) !== JSON.stringify(savedSettings.business_price.price_roundoff_value)
                // ONLY COMPARE VALUE OF BUSINESS PRICE IF ITS ATTRIBUTE IS NOT DEFAULT
                if (settings.business_price.attribute !== 'default')
                    contextualObj.priceSettings_businessPrice_value = settings.business_price.value !== savedSettings.business_price.value
            }
            // COMPARE ATTRIBUTE AND VALUE OF MINIMUM PRICE IF ITS ENABLED
            if (settings.settings_selected.minimum_price) {
                // COMPARE ATTRIBUTE OF MINIMUM PRICE
                contextualObj.priceSettings_minimumPrice_attribute = settings.minimum_price.attribute !== savedSettings.minimum_price.attribute
                // COMPARE ROUND OFF VALUE FOR MINIMUM PRICE
                contextualObj.priceSettings_minimumPrice_roundOffValue = JSON.stringify(settings.minimum_price.price_roundoff_value) !== JSON.stringify(savedSettings.minimum_price.price_roundoff_value)
                // ONLY COMPARE VALUE OF MINIMUM PRICE IF ITS ATTRIBUTE IS NOT DEFAULT
                if (settings.minimum_price.attribute !== 'default')
                    contextualObj.priceSettings_minimumPrice_value = settings.minimum_price.value !== savedSettings.minimum_price.value
            }
            // COMPARE ATTRIBUTE AND VALUE OF SALE PRICE IF ITS ENABLED
            if (settings.settings_selected.sale_price) {
                // COMPARE ATTRIBUTE OF SALE PRICE
                contextualObj.priceSettings_salePrice_attribute = settings.sale_price.attribute !== savedSettings.sale_price.attribute
                // COMPARE ROUND OFF VALUE FOR SALE PRICE
                contextualObj.priceSettings_salePrice_roundOffValue = JSON.stringify(settings.sale_price.price_roundoff_value) !== JSON.stringify(savedSettings.sale_price.price_roundoff_value)
                // COMPARE ATTRIBUTE OF SALE PRICE
                contextualObj.priceSettings_salePrice_startDate = settings.sale_price.start_date !== savedSettings.sale_price.start_date
                // COMPARE ATTRIBUTE OF SALE PRICE
                contextualObj.priceSettings_salePrice_endDate = settings.sale_price.end_date !== savedSettings.sale_price.end_date
                // ONLY COMPARE VALUE OF SALE PRICE IF ITS ATTRIBUTE IS NOT DEFAULT
                if (settings.sale_price.attribute !== 'default')
                    contextualObj.priceSettings_salePrice_value = settings.sale_price.value !== savedSettings.sale_price.value
            }
        }
        return JSON.parse(JSON.stringify(contextualObj))
    }
    const onPriceSettingsChange = (key: string, subKey: string, value: any) => {
        let tempPriceSettings = { ...priceSettings };
        let priceSettingsObj = defaultTemplatePriceFormatter(templateId);
        if (subKey) {
            tempPriceSettings[key][subKey] = value;
        }
        else {
            tempPriceSettings[key] = value;
        }
        setContextualSaveBarData((a: any) => ({ ...a, ...comparePriceSettings(tempPriceSettings, priceSettingsObj[0]?.data) }))
        setPriceSettings({ ...tempPriceSettings });
    }
    const resetPriceSettingsToDefault = (key: string) => {
        let tempPriceSettings: any = { ...priceSettings };
        tempPriceSettings[key] = { ...defaultPriceSettings[key] };
        setPriceSettings({ ...tempPriceSettings });
        let priceSettingsObj = defaultTemplatePriceFormatter(templateId);
        if (JSON.stringify(priceSettingsObj?.[0]?.data) != JSON.stringify(tempPriceSettings)) handleContextualSaveBar("priceSettings", true);
        else handleContextualSaveBar("priceSettings", false);
    }
    const formatPriceSettings = (initSettings: any, from: string) => {
        let tempPriceSettings: any = { ...initSettings }

        if (!tempPriceSettings.settings_enabled) {
            tempPriceSettings = {
                "data_type": "price_settings",
                "settings_enabled": false,
                "settings_selected": {
                    "sale_price": false,
                    "business_price": false,
                    "minimum_price": false
                },
                "standard_price": {
                    "attribute": "default",
                    "value": "",
                    "price_roundoff_value": '',
                },
                "sale_price": {
                    "attribute": "default",
                    "value": "",
                    "price_roundoff_value": '',
                    "start_date": "",
                    "end_date": ""
                },
                "business_price": {
                    "attribute": "default",
                    "value": "",
                    "price_roundoff_value": '',
                    "fixed_discount": [
                        {
                            "quantity": "",
                            "price": ""
                        }
                    ],
                    "percentage_discount": [
                        {
                            "quantity": "",
                            "price": ""
                        }
                    ],
                    "quantity_type": "fixed",
                },
                "minimum_price": {
                    "attribute": "default",
                    "value": "",
                    "price_roundoff_value": '',
                }
            }
        } else {
            if (!tempPriceSettings.settings_selected.sale_price) {
                tempPriceSettings.sale_price = {
                    "attribute": "default",
                    "price_roundoff_value": '',
                    "value": "",
                    "start_date": "",
                    "end_date": ""
                }
            }
            if (!tempPriceSettings.settings_selected.business_price) {
                tempPriceSettings.business_price = JSON.parse(JSON.stringify(defaultPriceSettings.business_price))
            }
            else {
                if (from == "get") {
                    let tempErrors = JSON.parse(JSON.stringify(errors));
                    tempPriceSettings['business_price'] = JSON.parse(JSON.stringify(defaultPriceSettings['business_price']));
                    if (initSettings?.hasOwnProperty('business_price') && initSettings?.business_price?.hasOwnProperty('attribute')) tempPriceSettings['business_price']['attribute'] = initSettings?.business_price?.attribute;
                    if (initSettings?.hasOwnProperty('business_price') && initSettings?.business_price?.hasOwnProperty('value')) tempPriceSettings['business_price']['value'] = initSettings?.business_price?.value;
                    if (initSettings?.hasOwnProperty('business_price') && initSettings?.business_price?.hasOwnProperty('quantity_type') && initSettings?.business_price?.quantity_type != "") {
                        tempPriceSettings['business_price']['quantity_type'] = initSettings?.business_price?.quantity_type;
                        setShowBusinessPrice(true);
                        setSavedShowBusinessPrice(true);
                        if (initSettings?.business_price?.quantity_discount) {
                            let err = initSettings?.business_price?.quantity_discount.map((discountObj: any) => {
                                return {
                                    "quantity": false,
                                    "price": false,
                                    "touched": {
                                        "quantity": true,
                                        "price": true
                                    }
                                }
                            })
                            if (initSettings?.business_price?.quantity_type == 'fixed') {
                                tempPriceSettings = {
                                    ...tempPriceSettings,
                                    business_price: {
                                        ...tempPriceSettings.business_price,
                                        fixed_discount: initSettings?.business_price?.quantity_discount
                                    }
                                }
                                tempErrors.pricing_settings.business_price.fixed_discount = err;
                            }
                            else {
                                tempPriceSettings = {
                                    ...tempPriceSettings,
                                    business_price: {
                                        ...tempPriceSettings.business_price,
                                        percentage_discount: initSettings?.business_price?.quantity_discount
                                    }
                                }
                                tempErrors.pricing_settings.business_price.percentage_discount = err
                            }
                            setErrors(JSON.parse(JSON.stringify(tempErrors)))
                        }
                    }
                }
                else if (from == "save") {
                    if (initSettings?.business_price?.attribute == "default") {
                        tempPriceSettings.business_price.value = "";
                    }
                }

                if (initSettings?.hasOwnProperty('business_price') && initSettings?.business_price?.hasOwnProperty('price_roundoff_value')) {
                    tempPriceSettings['business_price']['price_roundoff_value'] = initSettings?.business_price?.price_roundoff_value ?? '';
                }
            }
            if (!tempPriceSettings.settings_selected.minimum_price) {
                tempPriceSettings.minimum_price = {
                    "attribute": "default",
                    "price_roundoff_value": '',
                    "value": "",
                }
            }
        }
        return tempPriceSettings
    }

    function defaultTemplatePriceFormatter(id: string | null) {
        let priceSettingsObj: any = [];
        if (id) {
            if (id === 'default') {
                let tempPriceObj: any = { ...defaultPriceSettings }
                Object.values(savedProfileData).map((obj: any) => {
                    if (obj['key'] == "price" && obj?.['value'] && Object.keys(obj['value'])?.length > 0) {
                        if (obj?.value?.hasOwnProperty('data_type')) tempPriceObj['data_type'] = obj?.['value']?.data_type
                        if (obj?.value?.hasOwnProperty('settings_enabled')) tempPriceObj['settings_enabled'] = obj?.['value']?.settings_enabled
                        if (obj?.value?.hasOwnProperty('settings_selected')) tempPriceObj['settings_selected'] = obj?.['value']?.settings_selected
                        if (obj?.value?.hasOwnProperty('standard_price')) tempPriceObj['standard_price'] = obj?.['value']?.standard_price
                        if (obj?.value?.hasOwnProperty('sale_price')) tempPriceObj['sale_price'] = obj?.['value']?.sale_price
                        if (obj?.value?.hasOwnProperty('business_price')) tempPriceObj['business_price'] = obj?.['value']?.business_price
                        if (obj?.value?.hasOwnProperty('minimum_price')) tempPriceObj['minimum_price'] = obj?.['value']?.minimum_price
                        priceSettingsObj[0] = {
                            data: { ...tempPriceObj },
                            data_type: obj?.['value']?.data_type ?? ''
                        }
                    }
                });
            } else {
                priceSettingsObj = savedProfileData?.data?.filter((obj: any) => obj?.data_type == "pricing_settings")
            }
        } else {
            priceSettingsObj[0] = {
                data: { ...JSON.parse(JSON.stringify(defaultPriceSettings)) }
            }
        }
        return priceSettingsObj;
    }

    useEffect(() => {
        let tempErrors: any = JSON.parse(JSON.stringify(errors));
        const discountType = priceSettings?.business_price?.quantity_type == "fixed" ? "fixed_discount" : "percentage_discount";

        // if (discountType == "percentage_discount") tempErrors.business_price[discountType][index]["price"] = e == "" || e <= 0 || e >= 100
        // else tempErrors.business_price[discountType][index]["price"] = e == "" || e <= 0
        tempErrors.pricing_settings.business_price[discountType] = validatePriceEntries(priceSettings?.business_price, {}, () => { }, "global", priceSettings?.business_price?.[discountType], tempErrors.pricing_settings.business_price[discountType], "update", t);
        setErrors(tempErrors)
    }, [priceSettings?.business_price])


    const enableDisableContextualSaveBarForPriceSettings = (key: string, newValue: any) => {
        let oldValue = { ...defaultPriceSettings };
        if (templateId == 'default') {
            Object.values(savedProfileData).map((obj: any) => {
                if (obj['key'] == "price" && obj?.['value'] && Object.keys(obj['value'])?.length > 0) {
                    if (obj?.value?.hasOwnProperty('data_type')) oldValue['data_type'] = obj?.['value']?.data_type
                    if (obj?.value?.hasOwnProperty('settings_enabled')) oldValue['settings_enabled'] = obj?.['value']?.settings_enabled
                    if (obj?.value?.hasOwnProperty('settings_selected')) oldValue['settings_selected'] = obj?.['value']?.settings_selected
                    if (obj?.value?.hasOwnProperty('standard_price')) oldValue['standard_price'] = obj?.['value']?.standard_price
                    if (obj?.value?.hasOwnProperty('sale_price')) oldValue['sale_price'] = obj?.['value']?.sale_price
                    if (obj?.value?.hasOwnProperty('business_price')) oldValue['business_price'] = obj?.['value']?.business_price
                    if (obj?.value?.hasOwnProperty('minimum_price')) oldValue['minimum_price'] = obj?.['value']?.minimum_price
                }
            });
        }
        else {
            savedProfileData?.['data'].map((obj: any) => {
                if (obj['data_type'] == key) {
                    oldValue = { ...obj['data'] };
                }
            });
        }
        if (JSON.stringify(newValue) != JSON.stringify(oldValue)) handleContextualSaveBar("priceSettings", true);
        else handleContextualSaveBar("priceSettings", false)
    }


    const [filterSettings, setFilterSettings] = useState<any>(JSON.parse(JSON.stringify(defaultFilterSettings)));
    const onFilterSettingsChange = (key: string, value: any, from: string = "") => {
        let tempfilterSettings = JSON.parse(JSON.stringify(filterSettings));
        filterSettings[key] = value;
        setFilterSettings(JSON.parse(JSON.stringify(filterSettings)));
    }

    //manual template ke case mei jab kisi dusri template ka product isme show kare lekin assign na ho to useForceQuery true jana chye
    useEffect(() => {

        if (isAnotherProfileProductExists) {
            setManualProductInAnotherTemplate(true);
        }
    }, [isAnotherProfileProductExists]);

    const [categoryAndAttributesSettings, setCategoryAndAttributesSettings] = useState<any>({ ...JSON.parse(JSON.stringify(defaultCategoryAndAttributesSettings)) })
    const onCategorySettingsChange = (key: string, value: any) => {
        let tempcategoryAndAttributesSettings = { ...categoryAndAttributesSettings };
        categoryAndAttributesSettings['category_settings'][key] = value;
        setCategoryAndAttributesSettings({ ...categoryAndAttributesSettings });
        enableDisableContextualSaveBarForCategorySettings({ ...categoryAndAttributesSettings['category_settings'] })
    }
    const enableDisableContextualSaveBarForCategorySettings = (newValue: any) => {
        let tempSavedData = { ...savedProfileData };
        let discardStateCompare: any = {
            "primary_category": "",
            "sub_category": "",
            "browser_node_id": "",
            "barcode_exemption": false,
            "displayPath": [],
            "parentNodes": [],
            "type": ""
        }
        if (JSON.stringify(discardStateCompare) === JSON.stringify(newValue) && !props?.match?.t_id) {
            tempSavedData['category_id'] = { ...discardStateCompare }
        }
        if (JSON.stringify(tempSavedData?.['category_id']) != JSON.stringify(newValue)) handleContextualSaveBar("categorySettings", true);
        else deleteInContextualSaveBar("categorySettings");
    }
    const onAttributesSettingsChange = (key: string, value: any) => {
        let tempcategoryAndAttributesSettings = { ...categoryAndAttributesSettings };
        categoryAndAttributesSettings['attributes_mapping'][key] = value;
        setCategoryAndAttributesSettings({ ...categoryAndAttributesSettings });
    }
    const enableDisableContextualSaveBarForAttributesSettings = (newValue: any) => {
        let tempSavedData = { ...savedProfileData };
        let diff = getObjectDiff(newValue, {
            "required_attribute": tempSavedData['attributes_mapping']['required_attribute'],
            "optional_attribute": tempSavedData['attributes_mapping']['optional_attribute']
        })
        if (diff.length == 0) handleContextualSaveBar("attributesSettings", false)
        else handleContextualSaveBar("attributesSettings", true);
    }

    const validator = () => {
        let hasError = false;
        let category_settings = categoryAndAttributesSettings.category_settings;
        let attributes_mapping = categoryAndAttributesSettings.attributes_mapping;
        let tempErrors: any = { ...JSON.parse(JSON.stringify(errors)) };
        let clickResetFilterError: boolean = false;

        // meta field array prepare
        let meta_attributes: any = shopifyAttributes?.hasOwnProperty('Meta attributes') ? shopifyAttributes['Meta attributes'] : [];
        let meta_attributes_array: any = [];
        meta_attributes?.length > 0 && meta_attributes?.map((event: any) => {
            meta_attributes_array.push(event?.code)
        })

        if (templateId != "default") {
            if (templateName == "" || templateName.trim().length === 0 || templateName.trim().length > 32) {
                hasError = true;
                tempErrors.template_name = true;
            }
            Object.keys(category_settings).map((categoryKey) => {
                switch (categoryKey) {
                    case "primary_category":
                        tempErrors['category_settings']["primary_category"] =
                            category_settings[categoryKey] === "";
                        if (!hasError)
                            hasError =
                                category_settings[categoryKey] === "";
                        break;
                    case "sub_category":
                        tempErrors['category_settings']["sub_category"] =
                            category_settings["primary_category"] !== "" &&
                            category_settings[categoryKey] === "";
                        if (!hasError)
                            hasError =
                                category_settings["primary_category"] !== "" &&
                                category_settings[categoryKey] === "";
                        break;
                    case "browser_node_id":
                        tempErrors['category_settings']["browser_node_id"] = category_settings[categoryKey] === "";
                        if (!hasError)
                            hasError = category_settings[categoryKey] === "";
                        break;
                    case "parentNodes":
                        if (!hasError && category_settings['primary_category'] != "default" && category_settings['sub_category'] != "default") {
                            hasError = category_settings[categoryKey].length == 0;
                        }
                    case "displayPath":
                        if (!hasError && category_settings['primary_category'] != "default" && category_settings['sub_category'] != "default") {
                            hasError = category_settings[categoryKey].length == 0;
                        }
                    case "product_type":
                        tempErrors['category_settings']["product_type"] = category_settings?.product_type == undefined || (Array.isArray(category_settings?.product_type) ? !category_settings?.product_type?.[0] : category_settings?.product_type == "");
                        if (!hasError) {
                            hasError = category_settings?.product_type == undefined || (Array.isArray(category_settings?.product_type) ? !category_settings?.product_type?.[0] : category_settings?.product_type == "");
                        }
                    default: break;
                }
            });
            tempErrors['category_settings1'] = category_settings['primary_category'] == "" || category_settings['sub_category'] == "" || category_settings['browser_node_id'] == "" || (Array.isArray(category_settings?.product_type) ? !category_settings?.product_type?.[0] : category_settings?.product_type == "") ||
                category_settings['primary_category'] == undefined || category_settings['sub_category'] == undefined || category_settings['browser_node_id'] == undefined || category_settings?.product_type == undefined;
            if (!hasError) {
                hasError = category_settings['primary_category'] == "" || category_settings['sub_category'] == "" || category_settings['browser_node_id'] == "" || (category_settings?.product_type == undefined || (Array.isArray(category_settings?.product_type) ? !category_settings?.product_type?.[0] : category_settings?.product_type == ""));
            }
            if (category_settings["browser_node_id"] !== "") {
                if (!hasError && category_settings['primary_category'] != "default" && category_settings['sub_category'] != "default") {
                    if (category_settings['parentNodes'].length == 0 || category_settings['displayPath'].length == 0) hasError = true;
                }
                if (!JSONFeedAttributesData) {
                    let reqArr = [], optArr = [];
                    for (let i = 0; i < categoryAndAttributesSettings.attributes_mapping.required_attribute.length; i++) {
                        reqArr.push({
                            amazon_attribute: false,
                            shopify_attribute: false,
                            recommendation: false,
                            custom_text: false,
                            shopify_select: false,
                        });
                    }
                    for (let i = 0; i < categoryAndAttributesSettings.attributes_mapping.optional_attribute.length; i++) {
                        optArr.push({
                            amazon_attribute: false,
                            shopify_attribute: false,
                            recommendation: false,
                            custom_text: false,
                            shopify_select: false,
                        });
                    }
                    tempErrors.category_settings.attributes_mapping['required_attribute'] = reqArr;
                    tempErrors.category_settings.attributes_mapping['optional_attribute'] = optArr;
                    ["required_attribute", "optional_attribute"].forEach((attributeType) => {
                        if (attributes_mapping[attributeType].length > 0) {
                            //this function add meta field exist value
                            attributes_mapping[attributeType] = validateAndUpdateMetaAttributeExistance(attributes_mapping[attributeType], meta_attributes_array)
                            attributes_mapping[attributeType].map((objAttribute: any, index: number) => {
                                if (objAttribute['shopify_select'] === 'custom') {
                                    objAttribute['shopify_attribute'] = ''
                                } else if (objAttribute['shopify_select'] === 'Search') {
                                    objAttribute['custom_text'] = ''
                                }
                                let {
                                    amazon_attribute,
                                    shopify_attribute,
                                    recommendation,
                                    custom_text,
                                    shopify_select,
                                } = objAttribute;

                                //amazon_attribute error_code
                                if (!hasError) {
                                    hasError = amazon_attribute === "";
                                }
                                tempErrors.category_settings.attributes_mapping[attributeType][index]["amazon_attribute"] = amazon_attribute === "";

                                //shopify_select error_code
                                if (!hasError) {
                                    hasError = shopify_select === "";
                                }
                                tempErrors.category_settings.attributes_mapping[attributeType][index]["shopify_select"] = shopify_select === "";

                                //shopify_attribute error_code
                                if (!hasError) {
                                    hasError = shopify_select === "Search" && shopify_attribute === "";
                                }
                                tempErrors.category_settings.attributes_mapping[attributeType][index]["shopify_attribute"] = shopify_select === "Search" && shopify_attribute === "";

                                //recommendation error_code
                                if (!hasError) {
                                    hasError = shopify_select === "recommendation" && recommendation === "";
                                }
                                tempErrors.category_settings.attributes_mapping[attributeType][index]["recommendation"] = shopify_select === "recommendation" && recommendation === "";

                                //custom_text error_code
                                if (!hasError) {
                                    hasError = shopify_select === "custom" && (custom_text === "" || custom_text.trim().length === 0);
                                }
                                tempErrors.category_settings.attributes_mapping[attributeType][index]["custom_text"] = shopify_select === "custom" && (custom_text === "" || custom_text.trim().length === 0);
                            });
                        }
                    });
                }
            }
            if (filterSettings['filter_type'] == "advanced") {
                let bigArr = [];
                if (filterSettings?.['query'] === savedProfileData?.query && filterSettings?.['query'] === '' && (JSON.stringify(filterSettings?.queryArray) === JSON.stringify([[{ attribute: "", condition: "", value: "" }]])) && !isOpen) {
                    setOnClickResetFilters(true);
                    clickResetFilterError = true;
                } else {
                    for (let i = 0; i < filterSettings['queryArray'].length; i++) {
                        let smallArr = [];
                        for (let j = 0; j < filterSettings['queryArray'][i].length; j++) {
                            let obj = { attribute: false, condition: false, value: false };
                            smallArr.push(obj);
                        }
                        bigArr.push(smallArr);
                        tempErrors.filter_products.queryArray = bigArr;
                    }
                    filterSettings.queryArray.map((arr: any, grpIndex: number) => {
                        arr.map((obj: any, rowIndex: number) => {
                            let { attribute, condition, value } = obj;
                            tempErrors.filter_products.queryArray[grpIndex][rowIndex]['attribute'] = attribute == "";
                            tempErrors.filter_products.queryArray[grpIndex][rowIndex]['condition'] = condition == "";
                            tempErrors.filter_products.queryArray[grpIndex][rowIndex]['value'] = value.trim() == "";
                            if (!hasError) {
                                hasError = attribute == "" || condition == "" || value.trim() == "";
                            }
                        })
                    });
                    if (!hasError) {
                        hasError = filterSettings['query'] == "";
                    }
                }
            } else if (filterSettings['filter_type'] == "manual") {
                tempErrors['filter_products']['manual_product_ids'] = filterSettings['manual_product_ids'].length <= 0;
                if (JSON.stringify(filterSettings?.['manual_product_ids']) === JSON.stringify(savedProfileData?.manual_product_ids) && filterSettings?.['manual_product_ids']?.length === 0 && savedProfileData?.filter_type === '' && !isOpen) {
                    setOnClickResetFilters(filterSettings['manual_product_ids'].length <= 0);
                    clickResetFilterError = filterSettings['manual_product_ids'].length <= 0;
                }
                if (!hasError) {
                    hasError = filterSettings['manual_product_ids'].length <= 0;
                }
            }
        }

        tempErrors.inventory_settings.inventory_fulfillment_latency = inventorySettings.inventory_fulfillment_latency == "" || +inventorySettings.inventory_fulfillment_latency < 1 || +inventorySettings.inventory_fulfillment_latency > 30;
        if (!hasError) {
            hasError = inventorySettings.inventory_fulfillment_latency == "" || +inventorySettings.inventory_fulfillment_latency < 0 || +inventorySettings.inventory_fulfillment_latency > 30;
        }

        if (inventorySettings.inventory_settings) {
            if (inventorySettings.effective_inventory.enabled) {
                if (inventorySettings.effective_inventory.type == "none") {
                    if (!hasError) hasError = true;
                    tempErrors.inventory_settings.effective_inventory = true;
                }
                else {
                    if (inventorySettings.effective_inventory.value == '' || inventorySettings.effective_inventory.value == 0) {
                        if (!hasError) hasError = true;
                        if (inventorySettings.effective_inventory.type == 'fixed') {
                            tempErrors.inventory_settings.fixed_inventory = true;
                        }
                        else if (inventorySettings.effective_inventory.type == 'reserved') {
                            tempErrors.inventory_settings.reserved_inventory = true;
                        }
                        else if (inventorySettings.effective_inventory.type == 'threshold') {
                            tempErrors.inventory_settings.threshold_inventory = true;
                        }
                    }
                }

            }
            tempErrors.inventory_settings.warehouses = inventorySettings.warehouses.length === 0;
            if (!hasError) hasError = inventorySettings.warehouses.length === 0;
        }

        if (priceSettings.settings_enabled) {
            if (priceSettings.standard_price.attribute !== "default") {
                tempErrors.pricing_settings.standard_price.value = priceSettings.standard_price.value === "" || priceSettings.standard_price.value == priceSettings.standard_price.attribute;
                tempErrors.pricing_settings.standard_price.attribute = priceSettings.standard_price.attribute === ""; //
                if (!hasError) hasError = priceSettings.standard_price.value === "" || priceSettings.standard_price.attribute === "" || priceSettings.standard_price.value == priceSettings.standard_price.attribute;
            }
            if (priceSettings.settings_selected.sale_price) {
                tempErrors.pricing_settings.sale_price.end_date = priceSettings.sale_price.end_date === "";
                if (!hasError) {
                    hasError = priceSettings.sale_price.start_date === "" || priceSettings.sale_price.end_date === "" || new Date(priceSettings.sale_price.start_date) > new Date(priceSettings.sale_price.end_date)
                    if (!templateId && !isClonedTemplate) {
                        hasError = hasError || new Date(new Date().toISOString().substring(0, 10)) > new Date(priceSettings.sale_price.start_date)
                        tempErrors.pricing_settings.sale_price.start_date = priceSettings.sale_price.start_date === "" || new Date(new Date().toISOString().substring(0, 10)) > new Date(priceSettings.sale_price.start_date)
                    } else {
                        let initialData = { ...savedProfileData };
                        if (templateId === 'default') {
                            initialData = Object.values(initialData)?.find((obj: any) => obj.key === 'price')
                            initialData = initialData?.value?.sale_price
                        } else {
                            initialData = (savedProfileData?.data?.find((obj: any) => obj.data_type === 'pricing_settings').data.sale_price)
                        }
                        if (initialData?.start_date !== priceSettings.sale_price.start_date) {
                            hasError = hasError || new Date(new Date().toISOString().substring(0, 10)) > new Date(priceSettings.sale_price.start_date)
                            tempErrors.pricing_settings.sale_price.start_date = priceSettings.sale_price.start_date === "" || new Date(new Date().toISOString().substring(0, 10)) > new Date(priceSettings.sale_price.start_date)
                        }
                    }
                }
                if (priceSettings.sale_price.attribute !== "default") {
                    tempErrors.pricing_settings.sale_price.value = priceSettings.sale_price.value === "" || priceSettings.sale_price.value == priceSettings.sale_price.attribute;
                    tempErrors.pricing_settings.sale_price.attribute = priceSettings.sale_price.attribute === ""; //
                    if (!hasError) {
                        hasError = priceSettings.sale_price.value === "" || priceSettings.sale_price.attribute === '' || priceSettings.sale_price.value == priceSettings.sale_price.attribute;
                    }
                }
            }

            if (priceSettings.settings_selected.business_price) {
                if (priceSettings.business_price.attribute !== "default") {
                    tempErrors.pricing_settings.business_price.value = priceSettings.business_price.value === "" || priceSettings.business_price.value == priceSettings.business_price.attribute;
                    tempErrors.pricing_settings.business_price.attribute = priceSettings.business_price.attribute === "";
                    if (!hasError) hasError ||= priceSettings.business_price.value === "" || priceSettings.business_price.attribute === "" || priceSettings.business_price.value == priceSettings.business_price.attribute;
                }

                if (showBusinessPrice) {
                    if (!priceSettings.business_price.quantity_type) {
                        tempErrors.pricing_settings.business_price.quantity_type = true;
                        hasError = true;
                    } else {
                        const quantity_type = priceSettings.business_price.quantity_type == "percent" ? "percentage_discount" : "fixed_discount";
                        const discountArray = priceSettings.business_price[quantity_type];

                        if (discountArray && discountArray.length > 0) {
                            // Use validatePriceEntries to perform the validation logic
                            const errorsArr = validatePriceEntries(
                                priceSettings.business_price,
                                {},
                                () => { },
                                "template",
                                discountArray,
                                tempErrors.pricing_settings.business_price[quantity_type],
                                "save",
                                t
                            );

                            // Update tempErrors with the validation results
                            if (priceSettings.business_price.quantity_type === "fixed") {
                                tempErrors.pricing_settings.business_price.fixed_discount = errorsArr;
                            } else if (priceSettings.business_price.quantity_type === "percent") {
                                tempErrors.pricing_settings.business_price.percentage_discount = errorsArr;
                            }

                            // Set hasError to true if any row has errors
                            if (!hasError) hasError = errorsArr.some((err: any) => err.hasError);
                        }
                    }
                }
            }

            if (priceSettings.settings_selected.minimum_price) {
                if (priceSettings.minimum_price.attribute !== "default") {
                    tempErrors.pricing_settings.minimum_price.value = priceSettings.minimum_price.value === "" || priceSettings.minimum_price.value == priceSettings.minimum_price.attribute;
                    tempErrors.pricing_settings.minimum_price.attribute = priceSettings.minimum_price.attribute === ""; //
                    if (!hasError)
                        hasError = priceSettings.minimum_price.value === "" || priceSettings.minimum_price.attribute === '' || priceSettings.minimum_price.value == priceSettings.minimum_price.attribute;
                }
            }
        }
        if (templateId == "default") {
            let noneSelected = Object.values(productSettings?.sync_status_with).every(element => element === false);
            if (noneSelected && templateId == "default") {
                tempErrors.sync_status_with = noneSelected;
                hasError = true;
            }
        }

        if (isOpen) {
            !hasError && (hasError = filterSettings['filter_type'] == '');
            tempErrors.filter_products = {
                ...tempErrors?.filter_products ?? {},
                none_selected: true
            }
        }

        return { hasError, tempErrors, clickResetFilterError };
    }

    const prepareDataFormat = (jsonFeed: any) => {
        if (inventorySettings.max_inventory_level === '' || inventorySettings.max_inventory_level == undefined) {
            inventorySettings.max_inventory_level = '999';
        }

        let category_id: any = {};
        if (categoryAndAttributesSettings.category_settings.browser_node_id === 'default' && categoryAndAttributesSettings.category_settings.primary_category === 'default' && categoryAndAttributesSettings.category_settings.sub_category === 'default') {
            category_id = {
                "primary_category": categoryAndAttributesSettings.category_settings.primary_category,
                "sub_category": categoryAndAttributesSettings.category_settings.sub_category,
                "browser_node_id": categoryAndAttributesSettings.category_settings.browser_node_id,
                "barcode_exemption": categoryAndAttributesSettings.category_settings.barcode_exemption,
                "displayPath": ['Default'],
                "parentNodes": [],
                "type": categoryAndAttributesSettings.category_settings.type,
                ...isNewTemplate && !!JSONFeedAttributesData ?
                    // ...savedProfileData?.attributes_mapping?.required_attribute?.length === 0 &&
                    //     savedProfileData?.attributes_mapping?.optional_attribute?.length === 0 && !!JSONFeedAttributesData ?
                    { "product_type": categoryAndAttributesSettings.category_settings.product_type?.[0] } : {},
            };
        } else {
            category_id = {
                "primary_category": categoryAndAttributesSettings.category_settings.primary_category,
                "sub_category": categoryAndAttributesSettings.category_settings.sub_category,
                "browser_node_id": categoryAndAttributesSettings.category_settings.browser_node_id,
                "barcode_exemption": categoryAndAttributesSettings.category_settings.barcode_exemption,
                "displayPath": categoryAndAttributesSettings.category_settings.displayPath,
                "parentNodes": categoryAndAttributesSettings.category_settings.parentNodes ?? [],
                "type": categoryAndAttributesSettings.category_settings.type,
                ...isNewTemplate && !!JSONFeedAttributesData ? { "product_type": categoryAndAttributesSettings.category_settings.product_type?.[0] } : {},
            };
        }

        let formattedPriceSettings = formatPriceSettings({ ...priceSettings }, "save");


        formattedPriceSettings.business_price.quantity_discount = formattedPriceSettings.business_price.quantity_type == "fixed" ? formattedPriceSettings.business_price.fixed_discount : formattedPriceSettings.business_price.percentage_discount;
        delete formattedPriceSettings.business_price.fixed_discount;
        delete formattedPriceSettings.business_price.percentage_discount;

        const configData: any = [
            {
                "data_type": "pricing_settings",
                "title": templateName + "_pricing_template",
                "data": formattedPriceSettings,
            },
            {
                "data_type": "inventory_settings",
                "title": templateName + "_inventory_template",
                "data": formatInventorySettings({ ...inventorySettings }),
            },
            {
                "data_type": "product_settings",
                "title": templateName + "_product_template",
                "data": productSettings,
            },
            {
                "data_type": "fulfillment_settings",
                "title": templateName + "_product_template",
                "data": {
                    fulfillment_type: fulfillmentType
                },
            },
        ]
        let data: any = {};
        if (templateId && !isClonedTemplate) {
            data = {
                "_id": savedProfileData["_id"],
                "user_id": savedProfileData["user_id"],
                "name": templateName.trim(),
                "fulfillment_type": fulfillmentType,
                "query": filterSettings['query'],
                "queryArray": filterSettings['queryArray'],
                "manual_product_ids": filterSettings['manual_product_ids'],
                "manual_search": filterSettings['manual_search'],
                "category_id": category_id,
                "overWriteExistingProducts": filterSettings['overWriteExistingProducts'],
                "data": configData,
                "attributes_mapping": {
                    ...categoryAndAttributesSettings.attributes_mapping,
                    ...category_id?.product_type ?
                        {
                            jsonFeed: jsonFeed?.data,
                            language_tag: jsonFeed?.language_tag,
                            marketplace_id: jsonFeed?.marketplace_id,
                            optional_attribute: [],
                            required_attribute: []
                        }
                        : savedProfileData?.category_id?.hasOwnProperty('product_type') ? { jsonFeed: {} } : {},
                    "_id": savedProfileData['attributes_mapping']['_id'],
                },
                "filter_type": filterSettings['filter_type'] ? filterSettings['filter_type'] : (filterSettings['type'] ?? ''),
                "targets": [
                    {
                        "target_marketplace": "amazon",
                        "skip_query": "",
                        "category_id": category_id,
                        "data": configData,
                        "attributes_mapping": {
                            ...categoryAndAttributesSettings.attributes_mapping,
                            ...category_id?.product_type ?
                                {
                                    jsonFeed: jsonFeed?.data,
                                    language_tag: jsonFeed?.language_tag,
                                    marketplace_id: jsonFeed?.marketplace_id,
                                    optional_attribute: [],
                                    required_attribute: []
                                } : savedProfileData?.category_id?.hasOwnProperty('product_type') ? { jsonFeed: {} } : {},
                            "_id": savedProfileData['attributes_mapping']['_id']
                        },
                        "shops": [
                            {
                                "_id": savedProfileData['targets'][0]['shops'][0]['_id'],
                                "type": savedProfileData['targets'][0]['shops'][0]['type'],
                                "profile_id": savedProfileData['targets'][0]['shops'][0]['profile_id'],
                                "shop_id": props?.redux?.current?.target?.['_id'],
                                "active": 1,
                                "warehouses": [{
                                    "_id": savedProfileData['targets'][0]['shops'][0]['warehouses'][0]['_id'],
                                    "type": savedProfileData['targets'][0]['shops'][0]['warehouses'][0]['type'],
                                    "profile_id": savedProfileData['targets'][0]['shops'][0]['warehouses'][0]['profile_id'],
                                    "warehouse_id": "*warehouse_id",
                                    "active": 1,
                                    "data": configData,
                                    "attributes_mapping": {
                                        ...categoryAndAttributesSettings.attributes_mapping,
                                        ...category_id?.product_type ?
                                            {
                                                jsonFeed: jsonFeed?.data,
                                                language_tag: jsonFeed?.language_tag,
                                                marketplace_id: jsonFeed?.marketplace_id,
                                                optional_attribute: [],
                                                required_attribute: []
                                            } : savedProfileData?.category_id?.hasOwnProperty('product_type') ? { jsonFeed: {} } : {},
                                        "_id": savedProfileData['attributes_mapping']['_id']
                                    },
                                    "sources": [{
                                        "source_marketplace": "shopify",
                                        "data": configData,
                                        "attributes_mapping": {
                                            ...categoryAndAttributesSettings.attributes_mapping,
                                            ...category_id?.product_type ?
                                                {
                                                    jsonFeed: jsonFeed?.data,
                                                    language_tag: jsonFeed?.language_tag,
                                                    marketplace_id: jsonFeed?.marketplace_id,
                                                    optional_attribute: [],
                                                    required_attribute: []
                                                } : savedProfileData?.category_id?.hasOwnProperty('product_type') ? { jsonFeed: {} } : {},
                                            "_id": savedProfileData['attributes_mapping']['_id']
                                        },
                                        "shops": [
                                            {
                                                "_id": savedProfileData['targets'][0]['shops'][0]['warehouses'][0]['sources'][0]['shops'][0]['_id'],
                                                "profile_id": savedProfileData['targets'][0]['shops'][0]['warehouses'][0]['sources'][0]['shops'][0]['profile_id'],
                                                "type": savedProfileData['targets'][0]['shops'][0]['warehouses'][0]['sources'][0]['shops'][0]['type'],
                                                "shop_id": props?.redux?.current?.source?.['_id'],
                                                "active": 1,
                                                "warehouses": [{
                                                    "_id": savedProfileData['targets'][0]['shops'][0]['warehouses'][0]['sources'][0]['shops'][0]['warehouses'][0]['_id'],
                                                    "type": savedProfileData['targets'][0]['shops'][0]['warehouses'][0]['sources'][0]['shops'][0]['warehouses'][0]['type'],
                                                    "profile_id": savedProfileData['targets'][0]['shops'][0]['warehouses'][0]['sources'][0]['shops'][0]['warehouses'][0]['profile_id'],
                                                    "warehouse_id": "*warehouse_id",
                                                    "active": 1,
                                                    "Rule": "Not decided yet",
                                                    "data": configData,
                                                    "attributes_mapping": {
                                                        ...categoryAndAttributesSettings.attributes_mapping,
                                                        ...category_id?.product_type ?
                                                            {
                                                                jsonFeed: jsonFeed?.data,
                                                                language_tag: jsonFeed?.language_tag,
                                                                marketplace_id: jsonFeed?.marketplace_id,
                                                                optional_attribute: [],
                                                                required_attribute: []
                                                            } : savedProfileData?.category_id?.hasOwnProperty('product_type') ? { jsonFeed: {} } : {},
                                                        "_id": savedProfileData['attributes_mapping']['_id']
                                                    },
                                                }]
                                            }
                                        ]
                                    }]
                                }]
                            }
                        ]
                    }
                ]
            }
        }
        else {
            data = {
                "name": templateName.trim(),
                "fulfillment_type": fulfillmentType,
                "query": filterSettings['query'],
                "queryArray": filterSettings['queryArray'],
                "manual_product_ids": filterSettings['manual_product_ids'],
                "category_id": category_id,
                "overWriteExistingProducts": filterSettings['overWriteExistingProducts'],
                "data": configData,
                "attributes_mapping": {
                    ...categoryAndAttributesSettings.attributes_mapping,
                    ...category_id?.product_type ?
                        {
                            jsonFeed: jsonFeed?.data,
                            language_tag: jsonFeed?.language_tag,
                            marketplace_id: jsonFeed?.marketplace_id,
                            optional_attribute: [],
                            required_attribute: []
                        } : savedProfileData?.category_id?.hasOwnProperty('product_type') ? { jsonFeed: {} } : {},
                },
                "filter_type": filterSettings['filter_type'] ? filterSettings['filter_type'] : (filterSettings['type'] ?? ''),
                "targets": [{
                    "target_marketplace": "amazon",
                    "skip_query": "",
                    "category_id": category_id,
                    "data": configData,
                    "attributes_mapping": {
                        ...categoryAndAttributesSettings.attributes_mapping,
                        ...category_id?.product_type ?
                            {
                                jsonFeed: jsonFeed?.data,
                                language_tag: jsonFeed?.language_tag,
                                marketplace_id: jsonFeed?.marketplace_id,
                                optional_attribute: [],
                                required_attribute: []
                            } : savedProfileData?.category_id?.hasOwnProperty('product_type') ? { jsonFeed: {} } : {},
                    },
                    "shops": [
                        {
                            "shop_id": props?.redux?.current?.target?.['_id'],
                            "active": 1,
                            "warehouses": [{
                                "warehouse_id": "*warehouse_id",
                                "active": 1,
                                "data": configData,
                                "attributes_mapping": {
                                    ...categoryAndAttributesSettings.attributes_mapping,
                                    ...category_id?.product_type ?
                                        {
                                            jsonFeed: jsonFeed?.data,
                                            language_tag: jsonFeed?.language_tag,
                                            marketplace_id: jsonFeed?.marketplace_id,
                                            optional_attribute: [],
                                            required_attribute: []
                                        } : savedProfileData?.category_id?.hasOwnProperty('product_type') ? { jsonFeed: {} } : {},
                                },
                                "sources": [{
                                    "source_marketplace": "shopify",
                                    "data": configData,
                                    "attributes_mapping": {
                                        ...categoryAndAttributesSettings.attributes_mapping,
                                        ...category_id?.product_type ?
                                            {
                                                jsonFeed: jsonFeed?.data,
                                                language_tag: jsonFeed?.language_tag,
                                                marketplace_id: jsonFeed?.marketplace_id,
                                                optional_attribute: [],
                                                required_attribute: []
                                            } : savedProfileData?.category_id?.hasOwnProperty('product_type') ? { jsonFeed: {} } : {},
                                    },
                                    "shops": [{
                                        "shop_id": props?.redux?.current?.source?.['_id'],
                                        "active": 1,
                                        "warehouses": [{
                                            "warehouse_id": "*warehouse_id",
                                            "active": 1,
                                            "Rule": "Not decided yet",
                                            "data": configData,
                                            "attributes_mapping": {
                                                ...categoryAndAttributesSettings.attributes_mapping,
                                                ...category_id?.product_type ?
                                                    {
                                                        jsonFeed: jsonFeed?.data,
                                                        language_tag: jsonFeed?.language_tag,
                                                        marketplace_id: jsonFeed?.marketplace_id,
                                                        optional_attribute: [],
                                                        required_attribute: []
                                                    } : savedProfileData?.category_id?.hasOwnProperty('product_type') ? { jsonFeed: {} } : {},
                                            },
                                        }]
                                    }]
                                }]
                            }]
                        }
                    ]
                }]
            }
        }
        let finalData: any = {
            "source": {
                "marketplace": "shopify",
                "shopId": props?.redux?.current?.source?.['_id']
            },
            "target": {
                "marketplace": "amazon",
                "shopId": props?.redux?.current?.target?.['_id']
            },
            "data": data
        }

        return finalData;
    }
    const prepareDefaultDataFormat = () => {
        if (inventorySettings.max_inventory_level === '' || inventorySettings.max_inventory_level == undefined) {
            inventorySettings.max_inventory_level = '999';
        }
        inventorySettings['settings_selected']['warehouses'] = true;
        const configData: any = [
            {
                "group_code": "price",
                "data": formatPriceSettings({ ...priceSettings }, "save"),
            },
            {
                "group_code": "inventory",
                "data": formatInventorySettings({ ...inventorySettings }),
            },
            {
                "group_code": "product",
                "data": productSettings,
            },
        ]
        let finalData: any = {
            "source": {
                "marketplace": "shopify",
                "shopId": props?.redux?.current?.source?.['_id']
            },
            "target": {
                "marketplace": "amazon",
                "shopId": props?.redux?.current?.target?.['_id']
            },
            "data": configData
        }
        return finalData;
    }

    const getProductsCount = async () => {
        let countN = 0;
        let source = {
            "marketplace": "shopify",
            "shopId": props?.redux?.current?.source?.['_id']
        }
        let target = {
            "marketplace": "amazon",
            "shopId": props?.redux?.current?.target?.['_id']
        }
        let response: any = await props.di.POST('connector/profile/getQueryProductsCount/', ({
            "query": backendQuery && backendQuery != "" ? backendQuery : "",
            "user_id": props?.redux?.user_id,
            "source": source,
            "target": target,
            "overWriteExistingProducts": filterSettings['overWriteExistingProducts'],
            "check_profile_id": templateId && templateId != "default" ? savedProfileData["_id"]?.['$oid'] : '',
            "useRefinProduct": true,
            // "global_and": "(status==Not_listed || status==Not Listed: Offer)"
        }))

        if (response?.success) {
            if (response?.data?.count) {
                countN = response?.data?.count ?? 0;
                return response?.data?.count;
            }
        }
    }

    const getQuery = (manualProductIds: string[]) => {
        let sentence: string[] = [];
        sentence = manualProductIds.map((id: string) => {
            return `container_id %LIKE% ${id}`
        })
        let finalSentence: string = `(${sentence.join(' || ')})`;
        return finalSentence;
    }

    let [profileid, setProfileId] = useState('');
    let [saveCallsuccess, setSaveCallSuccess] = useState<boolean>(false);
    let [checkProdCount, setCheckProdCount] = useState<boolean>(false);

    useEffect(() => {
        if (profileid !== "") {
            if (saveCallsuccess) {
                getProfileProductsCount(profileid)
                handleValueMappingModal(profileid)
            }
        }
    }, [productsAssigned])

    const getProfileProductsCount = (id: string, a?: string) => {
        let str = 'abc';
        let source = {
            "marketplace": "shopify",
            "shopId": props?.redux?.current?.source?.['_id']
        }
        let target = {
            "marketplace": "amazon",
            "shopId": props?.redux?.current?.target?.['_id']
        }
        props.di.POST('connector/profile/getProfileProductsCount', ({
            "profile_id": id,
            "user_id": props?.redux?.user_id,
            "source": source,
            "target": target,
            'useRefinProduct': true,
        })).then((response) => {
            if (response?.success) {
                setProductsAssigned(response?.count ?? 0);
                // setHasActiveProducts(response?.active_product ?? 0);
                if (a) {
                    setTimeout(() => {
                        setValueMappingLoader(true);
                        setCheckProdCount(true);
                    }, 2000)
                }
            }
        })
        return str;
    }


    const handleValueMappingModal = (id: string, a?: string) => {
        setLoadingModal(true);
        const myintervalId = setInterval(async () => {
            let { success, data: profileData, count } = await props.di.GET('connector/profile/getProfileData', { "id": id });
            if (success && !profileData?.[0]?.product_update_in_progress) {
                setLoadingProgress(100);
                setUpdateInprogress(profileData?.[0]?.product_update_in_progress);
                checkValueMappingCall == false && getProfileProductsCount(id, a);
                setValueMappingData({
                    id: id,
                    count: productsAssigned,
                    templateName: templateName,
                    attributesMappingId: profileData?.[0]?.attributes_mapping?._id?.$oid,
                    categoryInfo: profileData?.[0]?.category_id
                });
                clearInterval(myintervalId);

                setTimeout(() => {
                    setLoadingModal(false); setValueMappingModal(true);
                }, 2000)
                return;
            }
        }, 2500);
        setintervalId(myintervalId);
    }

    const getQueryProductsCountBeforSave = async (manualProductIds: any) => {
        // this function handle the manual filter, when queryProductCount is greater than assigned product
        let query: string = getQuery(manualProductIds);
        let count = 0;
        let source = {
            "marketplace": "shopify",
            "shopId": props?.redux?.current?.source?.['_id']
        }
        let target = {
            "marketplace": "amazon",
            "shopId": props?.redux?.current?.target?.['_id']
        }
        await props.di.POST('connector/profile/getQueryProductsCount/', ({
            "query": `(${query})`,
            "user_id": props?.redux?.user_id,
            "source": source,
            "target": target,
            "overWriteExistingProducts": filterSettings['overWriteExistingProducts'],
            "check_profile_id": isClonedTemplate ? "" : templateId && templateId != "default" ? savedProfileData["_id"]?.['$oid'] : '',
            "useRefinProduct": true,
            // "global_and": "(status==Not_listed || status==Not Listed: Offer)"
        })).then((response: any) => {
            if (response?.success) {
                if (response?.data?.count) {
                    count = response?.data?.count;
                }
            }
        });
        return count
    }

    async function useForceQueryUpdateValueCalc(saveProfilePayload: any, getQueryCount?: number) {
        let advanceQueryCount = getQueryCount ?? selectedProductCount;
        if (filterSettings['filter_type'] == "advanced") {
            if (productsAssigned != advanceQueryCount) return true
        } else if (filterSettings['filter_type'] == "manual") {
            if (isAnotherProfileProductExists) {
                return isAnotherProfileProductExists
            } else {
                let flag = await getQueryProductsCountBeforSave(saveProfilePayload?.data?.manual_product_ids)
                return flag > productsAssigned
            }
        }
        return false;
    }

    const saveProfileData = async (jsonFeed: any, getQueryCount?: number, flagForValidate?: boolean) => {
        let preparedData = prepareDataFormat(jsonFeed);
        preparedData['useRefinProduct'] = true;
        preparedData['return_affected_profiles'] = true;
        let useForceQueryUpdate = getQueryCount ? await useForceQueryUpdateValueCalc(preparedData, getQueryCount) : await useForceQueryUpdateValueCalc(preparedData);
        if (useForceQueryUpdate) {
            //if overExistingProduct is true and other profile product is selected in current profile
            preparedData['useForceQueryUpdate'] = useForceQueryUpdate
        }
        console.log(preparedData, isClonedTemplate, "preparedDatapreparedData");
        // return


        let url = "connector/profile/saveProfile/";

        if (isAdminLoggedIn() && flagForValidate) {
            url = "amazon/profile/saveProfile/";
            setvalidationModal({ open: false, actionLoader: false, validationRequired: false })
        }
        props.di.POST(url, preparedData).then((response: any) => {

            setSaveLoader(false);
            let a: string = 'save';
            setContextualSaveBarData({});
            if (response?.success) {
                // setLoadingModal(true);
                setProfileId(response?.profile_id?.$oid);
                if (isOpen) {
                    saveConfig(response?.profile_id?.$oid);
                    setTourLoading(false)
                }
                setSaveCallSuccess(true);
                handleValueMappingModal(response?.profile_id?.$oid, a);
                props.success(t(response?.message));
            }
            else {
                props.error(t(response?.message) ?? t(response?.code) ?? t("Oops! Some error occured while saving."));
                if (response?.data) {
                    setValidatorErrorAfterSave(response?.data ?? []);
                    props.error(t(response?.message) ?? t(response?.code) ?? t("Oops! Some error occured while saving."));
                    // setRenderJSONFeed(true);
                    // setCategoryProductType(a => ({ ...a, currentData: feedData?.data }))
                } else {
                    setValidatorErrorAfterSave([])
                    props.error(t('Error while saving!'));
                    props.error(t(response?.message) ?? t(response?.code) ?? t("Oops! Some error occured while saving."));
                    if (isOpen) {
                        setIsOpen(false);
                        setTourLoading(false);
                    }
                }
            }
        });
    }

    let [eligibleForMapping, setEligibleForMapping] = useState<boolean | string>('');
    let [checkValueMappingCall, setCheckvalueMappingCall] = useState<boolean>(false);

    useEffect(() => {
        if (checkProdCount && saveCallsuccess) {
            checkEligibleForValueMapping(profileid);
        }
    }, [checkProdCount])


    const checkEligibleForValueMapping = (id: string) => {
        setCheckvalueMappingCall(true);
        props?.di?.POST("amazon/product/valueMapping", {
            "target": {
                "marketplace": "amazon",
                "shopId": props?.redux?.current?.target?.['_id']
            },
            "source": {
                "marketplace": "shopify",
                "shopId": props?.redux?.current?.source?.['_id']
            },
            "profile_id": id,
            "json_listings": isNewTemplate
        }).then((data: any) => {
            setModalLoader(false);
            // let arr: any = [];
            setValueMappingLoader(false);
            if (data?.success) {
                if (data?.count == 0) {
                    setEligibleForMapping(false);
                    setValueMappingModal(true);
                }
                else {
                    setEligibleForMapping(true);
                }
            } else {
                setEligibleForMapping(false)
            }
            // setLoadingModal(false);
        })
    }

    const saveDefaultProfileData = () => {
        let preparedData = prepareDefaultDataFormat();
        props.di.POST('connector/profile/saveDefaultSettings/', preparedData).then((response: any) => {
            setSaveLoader(false);
            setShowContextualSaveBar(false);
            if (response?.success) {
                setTemplateId("");
                setTemplateId(props?.match?.t_id);
                setContextualSaveBarData({});
                props.success(t(response?.message) ?? t("Default settings saved successfully"));
            }
            else {
                props.error(t(response?.message) ?? t(response?.code) ?? t("Oops! Some error occured while saving."))
            }
        });
    }

    const saveProducttemplate = (flagForValidate?: boolean) => {
        setSaveLoader(true);
        if (isOpen) {
            setTourLoading(true);
        }
        let {
            hasError,
            tempErrors: errorsRecieved,
            clickResetFilterError
        } = validator();

        let feedData = JSONFeedRef.current && JSONFeedRef.current.saveJSONFeed(true);
        if (!!JSONFeedAttributesData && feedData) {
            !hasError && (hasError = feedData.hasError)
        }

        if (!hasError && !attributesLoader && !clickResetFilterError) {
            if (flagForValidate) {
                setvalidationModal({ ...validationModal, actionLoader: true })
            }
            if (isClonedTemplate) {
                props.di.POST('connector/profile/validateProfile', (
                    {
                        "validate_on": { "name": templateName.trim() },
                    }
                )).then((response: any) => {
                    if (response?.success == false) {
                        props.error(t("Template can't be created with duplicate name."));
                        setSaveLoader(false);
                    }
                    else {
                        if (filterSettings['filter_type'] == "manual" && filterSettings['manual_product_ids'].length == 0) {
                            props.error(t("Template can't be created with zero(0) products filtered."));
                            setSaveLoader(false);
                        }
                        else if (filterSettings['filter_type'] == "advanced") {
                            let source = {
                                "marketplace": "shopify",
                                "shopId": props?.redux?.current?.source?.['_id']
                            }
                            let target = {
                                "marketplace": "amazon",
                                "shopId": props?.redux?.current?.target?.['_id']
                            }
                            props.di.POST('connector/profile/getQueryProductsCount/', ({
                                "query": backendQuery && backendQuery != "" ? backendQuery : "",
                                "user_id": props?.redux?.user_id,
                                "source": source,
                                "target": target,
                                "overWriteExistingProducts": filterSettings['overWriteExistingProducts'],
                                "check_profile_id": isClonedTemplate ? '' : templateId && templateId != "default" ? savedProfileData["_id"]?.['$oid'] : '',
                                "useRefinProduct": true,
                                "useForceQueryUpdate": true,
                                // "global_and": "(status==Not_listed || status==Not Listed: Offer)"
                            })).then((response: any) => {
                                if (response?.success) {
                                    if (response?.data?.count == 0 || response?.data?.count == null) {
                                        props.error(t("Template can't be created with zero(0) products filtered."));
                                        setSaveLoader(false);
                                    }
                                    else {
                                        saveProfileData(feedData, undefined, flagForValidate);
                                        // setIsClonedTemplate(false);
                                    }
                                }
                                else {
                                    props.error(t("Template can't be created"));
                                    setSaveLoader(false);
                                }
                            });
                        }
                        else {
                            saveProfileData(feedData, undefined, flagForValidate);
                            // setIsClonedTemplate(false);
                        }
                    }
                });
            }
            else {
                if (templateId == "default") {
                    saveDefaultProfileData();
                }
                else {
                    let validateData = templateId === "" || !templateId ? { "validate_on": { "name": templateName.trim() } } : {
                        "validate_on": { "name": templateName.trim() },
                        "profile_id": templateId ?? ''
                    }
                    props.di.POST('connector/profile/validateProfile', (validateData)).then((response: any) => {
                        if (response?.success == false) {
                            props.error(t("Template can't be created with duplicate name."));
                            setSaveLoader(false);
                            if (isOpen) {
                                setTourLoading(false);
                                setCurrentStep(1)
                            }
                        }
                        else {
                            if (filterSettings['filter_type'] == "manual" && filterSettings['manual_product_ids'].length == 0) {
                                props.error(t("Template can't be created with zero(0) products filtered."));
                                setSaveLoader(false);
                            }
                            else if (filterSettings['filter_type'] == "advanced") {
                                let source = {
                                    "marketplace": "shopify",
                                    "shopId": props?.redux?.current?.source?.['_id']
                                }
                                let target = {
                                    "marketplace": "amazon",
                                    "shopId": props?.redux?.current?.target?.['_id']
                                }
                                props.di.POST('connector/profile/getQueryProductsCount/', ({
                                    "query": backendQuery && backendQuery != "" ? backendQuery : "",
                                    "user_id": props?.redux?.user_id,
                                    "source": source,
                                    "target": target,
                                    "overWriteExistingProducts": filterSettings['overWriteExistingProducts'],
                                    "check_profile_id": templateId && templateId != "default" ? savedProfileData["_id"]?.['$oid'] : '',
                                    "useRefinProduct": true,
                                    "useForceQueryUpdate": true,
                                    // "global_and": "(status==Not_listed || status==Not Listed: Offer)"
                                })).then((response: any) => {
                                    if (response?.success) {
                                        if (response?.data?.count == 0) {
                                            props.error(t("Template can't be created with zero(0) products filtered."));
                                            setSaveLoader(false);
                                        }
                                        else {
                                            if (templateId == "default") {
                                                saveDefaultProfileData();
                                            }
                                            else {
                                                saveProfileData(feedData, response?.data?.count, flagForValidate);
                                            }
                                        }
                                    }
                                    else {
                                        props.error(t("Template can't be created"));
                                        setSaveLoader(false);
                                    }
                                });
                            } else {
                                saveProfileData(feedData, undefined, flagForValidate);
                            }
                        }
                    });
                }
            }
        }
        else {
            setvalidationModal({ ...validationModal, open: false })
            if (isOpen) {
                setTourLoading(false);
            }
            if (clickResetFilterError) {
                setSaveLoader(false);
            } else {
                if (attributesLoader) props.error(t("Some fields are loading, please wait for a while."))
                else props.error(t("Some information is missing or wrong, kindly check them before proceeding."));
                setErrors({ ...errorsRecieved });
                setSaveLoader(false);
            }
        }
    }

    // const getBrowseNode = async (category_id: any) => {
    //     let extracted_category_data: any = [];
    //     let payload: any = {
    //         primary_category: category_id?.primary_category,
    //         sub_category: category_id?.sub_category,
    //         browser_node_id: category_id?.browser_node_id,
    //         user_id: props?.redux?.user_id,
    //         source: {
    //             "marketplace": "shopify",
    //             "shopId": props?.redux?.current?.source?.['_id']
    //         },
    //         target: {
    //             "marketplace": "amazon",
    //             "shopId": props?.redux?.current?.target?.['_id']
    //         },
    //     }

    //     await props.di.POST("amazon/product/browseNode", payload).then((data: any) => {
    //         let { success, data: responseData, response } = data;
    //         if (success) {
    //             if (Object.keys(response)?.length > 0 && response?.displayPath && Object.keys(response?.displayPath)?.length > 0 && response?.parentNodes && Object.keys(response?.parentNodes)?.length > 0) {
    //                 extracted_category_data = response;
    //             } else {
    //                 extracted_category_data = [];
    //                 props.error(t("Category Not Found"));
    //             }
    //         } else {
    //             props.error(t("Category Not Found"));
    //         }
    //     })
    //     return extracted_category_data;
    // }

    const formatSavedData = (savedData: any) => {
        let priceSettings = savedData.data.filter((dataObj: any) => dataObj.data_type == "pricing_settings")?.[0]?.data
        if (priceSettings?.hasOwnProperty('business_price') && priceSettings?.business_price?.hasOwnProperty('quantity_type')) {
            if (priceSettings?.business_price?.quantity_discount) {
                if (priceSettings?.business_price?.quantity_type == 'fixed') {
                    priceSettings = {
                        ...priceSettings,
                        business_price: {
                            ...priceSettings.business_price,
                            fixed_discount: priceSettings?.business_price?.quantity_discount,
                            percentage_discount: [{ price: '', quantity: '' }]

                        }
                    }
                }
                else if (priceSettings?.business_price?.quantity_type == 'percent') {
                    priceSettings = {
                        ...priceSettings,
                        business_price: {
                            ...priceSettings.business_price,
                            percentage_discount: priceSettings?.business_price?.quantity_discount,
                            fixed_discount: [{ price: '', quantity: '' }]
                        }
                    }
                }
            }
        }
        savedData.data.map((dataObj: any, index: number) => {
            if (dataObj.data_type == "pricing_settings") {
                savedData['data'][index] = {
                    data_type: "pricing_settings",
                    title: "business price_pricing_template",
                    data: priceSettings
                }
            }
        })
        return savedData
    }

    const getSavedProfile = async (id: string | null | undefined) => {
        setSaveLoader(true)
        let { success, data: profileData, count } = await props.di.GET('connector/profile/getProfileData', { "id": id, useRefinProduct: true });
        setShowSkeleton(false);
        if (success) {
            let data = { ...profileData[0] };
            let savedData = { ...savedProfileData };
            if (isClonedTemplate) {
                savedData = {
                    ...savedData, ...JSON.parse(JSON.stringify({
                        ...profileData[0],
                        name: "Copy of " + data['name'],
                        manual_product_ids: [],
                        manual_search: '',
                        query: '',
                        filter_type: '',
                        queryArray: [[{ attribute: "", condition: "", value: "" }]],
                    }))
                }
                savedData['category_id'] = {
                    ...savedData?.category_id,
                    product_type: data?.category_id?.product_type ? [data?.category_id?.product_type] : ''
                };
                setFulfillmentType(savedData?.['fulfillment_type'] ?? 'FBM');
                setSavedProfileData(savedData);
                setTemplateName("Copy of " + data['name']);
                setFilterSettings({
                    ...JSON.parse(JSON.stringify(defaultFilterSettings)),
                    overWriteExistingProducts: data['overWriteExistingProducts'],
                });
            }
            else {
                savedData = { ...savedData, ...JSON.parse(JSON.stringify({ ...profileData[0] })) }
                const formattedData: any = formatSavedData(savedData);
                formattedData['category_id'] = {
                    ...formattedData?.category_id,
                    product_type: data?.category_id?.product_type ? [data?.category_id?.product_type] : ''
                };
                setSavedProfileData(formattedData);
                setTemplateName(data['name']);
                setFulfillmentType(data?.['fulfillment_type'] ?? 'FBM');
                let tempfilterSettings = JSON.parse(JSON.stringify(filterSettings));
                setUpdateInprogress(data['product_update_in_progress']);
                if ((data['manual_product_ids'] && data['manual_product_ids'].length > 0) || data['filter_type'] === 'manual') {
                    tempfilterSettings['manual_product_ids'] = data?.['manual_product_ids'];
                    tempfilterSettings['manual_search'] = data?.['manual_search'] ?? "";
                    tempfilterSettings['type'] = "manual";
                }
                else if (data['query'] != '' && data['queryArray'] != '') {
                    tempfilterSettings['query'] = data?.['query'];
                    tempfilterSettings['queryArray'] = data?.['queryArray'];
                    tempfilterSettings['type'] = "advanced";
                } else {
                    tempfilterSettings = JSON.parse(JSON.stringify(defaultFilterSettings));
                }
                tempfilterSettings['overWriteExistingProducts'] = data['overWriteExistingProducts'];
                tempfilterSettings['filter_type'] = data['filter_type'];
                setFilterSettings(JSON.parse(JSON.stringify(tempfilterSettings)));
                getProfileProductsCount(data['_id']['$oid']);
            }
            let tempRoundOffUi = JSON.parse(JSON.stringify(roundOffUi))
            data['data'].map((obj: any) => {
                if (obj['data_type'] == "inventory_settings") {
                    let filterWarehouseData = filterDefaultWarehouse(props?.redux?.current?.source?.warehouses, obj?.['data']?.['warehouses'] ?? []);
                    obj['data']['warehouses'] = filterWarehouseData;
                    DisableWarehouseInitially(obj?.['data']?.['warehouses'], savedData);
                    setInventorySettings(formatInventorySettings({ ...obj['data'] }));
                }
                if (obj['data_type'] == "pricing_settings") {
                    if (obj?.data?.hasOwnProperty('sale_price')) {
                        tempRoundOffUi['sale_price']['price_roundoff_value'] = obj?.data?.sale_price?.price_roundoff_value;
                    }
                    if (obj?.data?.hasOwnProperty('business_price')) {
                        tempRoundOffUi['business_price']['price_roundoff_value'] = obj?.data?.business_price?.price_roundoff_value;
                    }
                    if (obj?.data?.hasOwnProperty('minimum_price')) {
                        tempRoundOffUi['minimum_price']['price_roundoff_value'] = obj?.data?.minimum_price?.price_roundoff_value;
                    }
                    if (obj?.data?.hasOwnProperty('standard_price')) {
                        tempRoundOffUi['standard_price']['price_roundoff_value'] = obj?.data?.standard_price?.price_roundoff_value;
                    }
                    setRoundOffUi(JSON.parse(JSON.stringify(tempRoundOffUi)))
                    setPriceSettings(formatPriceSettings({ ...obj['data'] }, "get"))
                }
                if (obj['data_type'] == "product_settings") {
                    setProductSettings({ ...productSettings, ...obj['data'] });
                }
            });

            let tempCategorySettings = { ...categoryAndAttributesSettings };
            let tempErrors = { ...JSON.parse(JSON.stringify(errors)) };
            if (data?.['category_id'] && data['category_id']?.['displayPath']?.length > 0) {
                tempCategorySettings['category_settings'] = { ...data?.category_id, product_type: [data?.category_id?.product_type] };
            }
            // else if (data?.category_id && data?.category_id?.primary_category !== 'default' && data?.category_id?.sub_category !== 'default') {
            //     let browseNodeResponse = await getBrowseNode(data?.category_id);
            //     tempCategorySettings['category_settings'] = { ...data?.category_id, product_type: [data?.category_id?.product_type] };
            //     tempCategorySettings['category_settings']['displayPath'] = browseNodeResponse?.displayPath;
            //     tempCategorySettings['category_settings']['parentNodes'] = browseNodeResponse?.parentNodes;
            //     tempCategorySettings['category_settings']['browser_node_id'] = browseNodeResponse?.browser_node_id;
            // }
            if (data['attributes_mapping'] && Object.keys(data['attributes_mapping']).length > 0) {
                tempCategorySettings['attributes_mapping'] = {
                    required_attribute: data?.attributes_mapping?.required_attribute || [],
                    optional_attribute: data?.attributes_mapping?.optional_attribute || []
                };
                tempErrors['category_settings']['attributes_mapping']['required_attribute'] = data?.attributes_mapping?.required_attribute?.map((obj: any) => {
                    return {
                        amazon_attribute: false,
                        shopify_attribute: false,
                        recommendation: false,
                        custom_text: false,
                        shopify_select: false,
                    }
                });
                tempErrors['category_settings']['attributes_mapping']['optional_attribute'] = data?.attributes_mapping?.optional_attribute?.map((obj: any) => {
                    return {
                        amazon_attribute: false,
                        shopify_attribute: false,
                        recommendation: false,
                        custom_text: false,
                        shopify_select: false,
                    }
                });
            }
            await getAllProductTypes().then((res: any) => {
                if (res?.success && res?.data?.length > 0) {
                    setAllProductTypes(res?.data);
                    setIsNewTemplate(!!templateId ? !!data?.category_id?.product_type : true);
                } else {
                    setIsNewTemplate(false);
                }
            })
            setCategoryAndAttributesSettings({ ...tempCategorySettings });
            setDefaultAttributes({ ...data?.attributes_mapping });
            data?.attributes_mapping?.jsonFeed && setSavedJSONFeedDataAttributes(data?.attributes_mapping?.jsonFeed ?? {});
            data?.attributes_mapping?.jsonFeed && setCurrentData(data?.attributes_mapping?.jsonFeed ?? {});
        }
        setSaveLoader(false)
    }

    async function getShopifyAttributes() {
        await props.di.POST('connector/product/getProductAttributes', { "source": { "marketplace": "shopify", "shopId": props?.redux?.current?.source?.['_id'] } }).then((response: any) => {
            if (response?.success && response?.data && Object.keys(response?.data).length > 0) {
                setShopifyAttributes({ ...response?.data });
            }
            setShopifyAttributesLoader(false);
        });
    }

    function DisableWarehouseInitially(warehouseSelected: any, savedData?: any) {
        if (warehouseSelected?.length == 1) {
            let tempWareHouseOptions: any = [...wareHouseOptions];
            tempWareHouseOptions?.map((event: any, index: any) => {
                if (warehouseSelected.includes(tempWareHouseOptions?.[index]?.value)) {
                    tempWareHouseOptions[index]['disabled'] = true;
                }
            })
            setwareHouseOptions([...tempWareHouseOptions]);
        } else {
            fillWareHouseSettings(savedData);
        }
    }

    function filterDefaultWarehouse(propsWarehouse: any, savedWarehouse: any) {
        let tempWarehouse: any = [];
        Object?.values(propsWarehouse)?.map((warehouse: any, index: number) => {
            if (savedWarehouse.includes(warehouse?.id) && warehouse?.active) {
                tempWarehouse.push(warehouse?.id);
            }
        })
        return tempWarehouse;
    }

    //agar ek hi warehouse save ho aur woh bhi disabled aye shopify se toh yeh condition implement ho
    const updateSingleWarehouseCondition = () => {
        let tempWarehouse: any = [];
        let activatedWarehouses: any = [];
        [...props?.redux?.current?.source?.warehouses]?.map((warehouse: any, index: number) => {
            if (warehouse?.active) {
                activatedWarehouses.push(warehouse);
            }
        })
        if (activatedWarehouses.length == 1) {
            tempWarehouse = [];
            tempWarehouse.push(activatedWarehouses[0]['id']);
        }
        let tempInventorySettings = JSON.parse(JSON.stringify(inventorySettings));
        tempInventorySettings['warehouses'] = tempWarehouse;
        setInventorySettings(JSON.parse(JSON.stringify(tempInventorySettings)));
    }

    const fillWareHouseSettings = (savedData?: any) => {
        //some code is commented for warehouse cases
        let warehouses = [...props?.redux?.current?.source?.warehouses];
        let activatedWarehouses: any = [];
        let arr: any = [];
        let selectedWareHouses: any = [];
        if (warehouses && Array.isArray(warehouses) && warehouses.length > 0) {
            arr = warehouses?.map((singlewarehouse: any) => {

                if (singlewarehouse?.name && singlewarehouse?.name !== '') {
                    let modifiedLabel = `${singlewarehouse.name} ${(singlewarehouse.address1 != undefined && singlewarehouse.address1 != null && singlewarehouse.address1 != '') ? '(' + singlewarehouse.address1 + ')' : ''}`;

                    if (singlewarehouse?.active) {
                        // activatedWarehouses.push(singlewarehouse);
                        selectedWareHouses.push((singlewarehouse?.id).toString())
                        return { label: modifiedLabel, value: (singlewarehouse.id).toString(), disabled: !singlewarehouse.active, deactivated: false }
                    }
                    else {
                        return { label: modifiedLabel + "(deactivated on shopify)", value: (singlewarehouse.id).toString(), disabled: !singlewarehouse.active, deactivated: true }
                    }
                }
            });
        }
        if (warehouses && Array.isArray(warehouses) && warehouses.length == 1) {
            arr[0]['disabled'] = true;
        }
        if (activatedWarehouses.length == 1) {
            selectedWareHouses = [];
            selectedWareHouses.push(activatedWarehouses[0]['id']);
            arr.map((reduxWarehouse: any) => {
                if (reduxWarehouse?.value == activatedWarehouses[0]['id']) {
                    reduxWarehouse.disabled = true;
                    reduxWarehouse.deactivated = true;
                }
            })
        }
        setwareHouseOptions(JSON.parse(JSON.stringify(arr)));
        let tempInventorySettings = JSON.parse(JSON.stringify(inventorySettings));
        tempInventorySettings['warehouses'] = JSON.parse(JSON.stringify(selectedWareHouses));

        setInventorySettings(JSON.parse(JSON.stringify(tempInventorySettings)))
        let tempSavedProfileData: any = JSON.parse(JSON.stringify({ ...(savedData ?? savedProfileData) }));
        tempSavedProfileData.data.map((data: any, index: number) => {
            if (data.data_type == "inventory_settings") {
                tempSavedProfileData.data[index].warehouses = [...selectedWareHouses];
            }
        })
        // tempSavedProfileData.data['inventory_settings']['warehouses'] = [...selectedWareHouses];
        setSavedProfileData(JSON.parse(JSON.stringify(tempSavedProfileData)))
    }

    const appendModalClass = () => {
        let modal = document.getElementsByClassName('Polaris-Modal-Dialog__Container');
        modal?.[0]?.classList?.add("Custom-Modal__Progress");
    }

    useEffect(() => {
        getShopifyAttributes();
        fillWareHouseSettings();
        setTemplateId(props?.match?.t_id);
        // props?.location?.state?.isProductActive && setHasActiveProducts(props?.location?.state?.isProductActive)
        setIsClonedTemplate(props?.location?.state?.clone ?? false);
        if (!props?.match?.t_id) {
            getAllProductTypes().then((response) => {
                if (response?.success && response?.data?.length > 0) {
                    setAllProductTypes(response?.data ?? [])
                    setIsNewTemplate(true);
                } else {
                    setIsNewTemplate(false);
                }
            })
        }
        return () => {
            clearInterval(intervalId);
            if (process.env.REACT_APP_PROD === "1" && !isAdminLoggedIn()) hideSaveBar();
            setContextualSaveBarData({});
            setErrors(JSON.parse(JSON.stringify(errorsObj)));
        };
    }, []);

    const getAllProductTypes = async () => {
        return await props.di.POST('amazon/request/getAllProductTypes', {
            "target": {
                marketplace: props?.redux?.current?.target?.marketplace ?? '',
                shopId: props?.redux?.current?.target?._id ?? '',
            },
            "source": {
                marketplace: props?.redux?.current?.source?.marketplace ?? '',
                shopId: props?.redux?.current?.source?._id ?? '',
            }
        })
    }

    const checkTemplateTypeAndGetData = (id: any) => {
        if (id) {
            // if (id == "default") {
            //     getSavedDefaultProfile();
            // }
            // else {
            getSavedProfile(id);
            // }
        }
    }

    useEffect(() => {
        checkTemplateTypeAndGetData(templateId);
    }, [templateId]);

    const handleDiscardBtn = () => {
        setJSONFeedDataAttributes({})
        setSavedJSONFeedDataAttributes({});
        setCurrentData({});
        setDiscardBtnClicked(true);
        setShowSkeleton(true);
        setShowContextualSaveBar(false);
        setContextualSaveBarData({});
        setErrors(JSON.parse(JSON.stringify(errorsObj)));
        setTemplateName('');
        setCategoryAndAttributesSettings({ ...JSON.parse(JSON.stringify(defaultCategoryAndAttributesSettings)) });
        setInventorySettings(JSON.parse(JSON.stringify(defaultInventorySettings)));
        setPriceSettings(JSON.parse(JSON.stringify(defaultPriceSettings)));
        setProductSettings(JSON.parse(JSON.stringify(defaultProductSettings)));
        setFilterSettings(JSON.parse(JSON.stringify(defaultFilterSettings)));
        checkTemplateTypeAndGetData(templateId);
        fillWareHouseSettings();
    }

    const getTemplateTitle = () => {
        let title: string = "";
        if (isClonedTemplate) {
            title = t("Clone Template");
        }
        else if (templateId == "default") title = t("Edit Default Template");
        else if (templateId && templateId != "") title = t("Edit Product Template");
        else title = t("Add New Template");
        return title;
    }

    const getTemplateDescription = () => {
        let description: string = "";
        if (isClonedTemplate) {
            description = t("Set Templates for your products to save time by eliminating redundancy every time you upload new products by predefining metrics like Inventory, & Price & set them as default to be implemented on the products. Product Templates help you manage/ update Product data for multiple products at once. These templates help sellers to fix their Inventory Settings, Price Settings, Mapping Attributes, etc. in one go for all listings");
        }
        else if (templateId == "default") description = t("Default Templates help you manage products globally as per default settings. This means that whatever presets you choose here are automatically applied to all the products in case no custom template is used. You can also edit/update your settings here as per business requirements.");
        else if (templateId && templateId != "") description = t("Save time and reduce redundancy when uploading products by setting templates. Predefine metrics like Inventory and Price, and apply them as defaults across your products. These templates enable streamlined management and updates for multiple products simultaneously, allowing you to address Inventory Settings, Price Settings, Mapping Attributes, and more across all listings in a single go.");
        else description = t("Set Templates for your products to save time by eliminating redundancy every time you upload new products by predefining metrics like Inventory, & Price & set them as default to be implemented on the products. Product Templates help you manage/ update Product data for multiple products at once. These templates help sellers to fix their Inventory Settings, Price Settings, Mapping Attributes, etc. in one go for all listings");
        return description;
    }

    const moveToTemplateGrid = () => {
        props.history('../config/templateSettings/new');
    }
    const saveConfig = (prof_id: any) => {
        props.di.POST('connector/config/saveConfig', {
            data: [
                {
                    data: JSON.parse(
                        JSON.stringify(
                            {
                                bulk_upload_tour: {
                                    parts_visited: 3,
                                },
                                template_created: true,
                                templateId: prof_id
                            }
                        )
                    ),
                    group_code: 'tour',
                },
            ],
        }).then(() => {
            setIsOpen(false);
            setCurrentStep(0)
        })
    }

    const askFortemplateValidation = () => {
        setvalidationModal({
            ...validationModal,
            open: true
        })

    }

    const moveToValueMapping = () => {
        clearInterval(intervalId);
        props.history(`../config/template/valueMapping/${valueMappingData['id']}`, {
            replace: false, state: {
                count: valueMappingData['count'], templateName: valueMappingData['templateName'], id: valueMappingData['id'], attributesMappingId: valueMappingData['attributesMappingId'], categoryInfo: valueMappingData['categoryInfo'], type: isNewTemplate ? "json" : "xml", product_type: categoryAndAttributesSettings?.category_settings?.product_type?.[0] ?? ''
            }
        });
    }

    const [showModalLoader, setModalLoader] = useState<boolean>(true);

    //Contextual Save Bar of App Bridge
    if (process.env.REACT_APP_PROD === "1" && !isAdminLoggedIn()) {
        let savebar_options: any = {
            loading: saveloader,
            attributesLoader: attributesLoader,
            disable_discard: !templateId && isOpen,
        };
        if (showContextualSaveBar) {
            showSaveBar(handleDiscardBtn, saveProducttemplate, { ...savebar_options });
        } else hideSaveBar();
    }

    if (showSkeleton && templateId) {
        return <>
            {<TemplateSkeleton type={templateId} templateId={templateId} getTemplateTitle={getTemplateTitle} getTemplateDescription={getTemplateDescription} />}
        </>
    }
    else {
        return (
            <React.Fragment>
                {(attributesLoader || filterAttributesLoader) && <Loading />}
                <Modal
                    open={validationModal?.open}
                    onClose={() => {
                        setvalidationModal({ open: false, actionLoader: false, validationRequired: false, });
                        //  moveToTemplateGrid();
                    }}
                    title={t("Save Template")}
                    primaryAction={{
                        content: t('Save & Exit'),
                        onAction: () => saveProducttemplate(),
                        loading: validationModal?.actionLoader ? false : saveloader

                    }}
                    secondaryActions={[
                        {
                            content: t('Validate & Save'),
                            onAction: () => saveProducttemplate(true),
                            loading: validationModal?.actionLoader,
                        }
                    ]}
                >

                    <Modal.Section>
                        <Text as="p">{t("Would you like to validate the attributes before saving the template, or save it directly?")}</Text>
                        {/* <Checkbox checked={validationModal?.validationRequired} onChange={() => setvalidationModal({ ...validationModal, validationRequired: !validationModal?.validationRequired })} label='Validate Template before save'></Checkbox> */}
                    </Modal.Section>
                </Modal>
                <Modal
                    open={loadingModal}
                    onClose={() => { setLoadingModal(false); moveToTemplateGrid(); clearInterval(intervalId); }}
                    title={t("Assigning products to New Template")}
                    secondaryActions={[
                        {
                            content: t('Move on to Template Grid'),
                            onAction: () => { setLoadingModal(false); moveToTemplateGrid(); clearInterval(intervalId); },
                        }
                    ]}
                >
                    <div className='bulktour_template_saving_modal'>
                        <Modal.Section>
                            <BlockStack gap="400">
                                <Text as='p'>{t("Please wait while the products are being imported into the template, or you can proceed to the 'Template Grid' where you'll be able to access this template later")}.</Text>
                                <BlockStack gap="100">
                                    <img style={{ height: '100px', width: '100px', margin: '10px auto 10px auto' }} src={loadingProgress == 0 ? LoadingDocTemplate : Tick} alt='Assigning products in template' />
                                    <div className={loadingProgress == 100 ? 'Polaris-Text__CustomGreen' : undefined}><Text variant='bodyLg' as="p" alignment='center' fontWeight='bold'>
                                        {loadingProgress == 0 ? t('Wait while we are assigning products...') : t('Products Assigning is Completed')}
                                    </Text>
                                    </div>
                                </BlockStack>
                            </BlockStack>
                        </Modal.Section>
                    </div>
                </Modal>
                <Modal
                    open={valueMappingModal}
                    onClose={() => { setValueMappingModal(false); moveToTemplateGrid(); }}
                    title={t("Assigning products to New Template")}
                    primaryAction={eligibleForMapping ? {
                        content: t('Map values'),
                        loading: valuemappingLoader,
                        onAction: moveToValueMapping,
                        disabled: !eligibleForMapping
                    } : undefined}
                    secondaryActions={[
                        {
                            content: t('Move on to Template Grid'),
                            onAction: () => { setValueMappingModal(false); moveToTemplateGrid(); },
                        }
                    ]}
                >
                    <div className='bulktour_template_value_mapping_modal'>
                        <Modal.Section>
                            {
                                showModalLoader ?
                                    <SkeletonBodyText lines={4} /> :
                                    eligibleForMapping ?
                                        <BlockStack gap='400'>
                                            <InlineStack gap='200' wrap={false}>
                                                <div><Icon source={CircleTickMinor} tone='success' /></div>
                                                <Text as='span' tone='success' fontWeight='semibold' variant='bodyLg'>{`${productsAssigned} ${t("products have been assigned to the template")} (${templateName}).`}</Text>
                                            </InlineStack>
                                            <Banner tone='info'>{t("Certain products require value mapping for their attributes to ensure proper alignment between Shopify and Amazon values. Click on 'Map values' to initiate the mapping process.")}</Banner>
                                        </BlockStack>
                                        :
                                        <Banner tone='warning'>
                                            <Text as='span' fontWeight='semibold'>{t("Not eligible for value mapping")}</Text>
                                        </Banner>
                            }
                        </Modal.Section>
                    </div>
                </Modal>
                <Page
                    backAction={
                        {
                            content: t("Template"),
                            onAction: () => {
                                if (props.location?.hasOwnProperty('state') && props.location?.state?.hasOwnProperty('fromListingGrid') && props.location.state.fromListingGrid) {
                                    props.history(-1);
                                } else {
                                    navigate("../config/templateSettings/new");
                                }
                            },
                        }
                    }
                    primaryAction={
                        <RenderHeadersAction {...props} disable_headers={true} />
                    }
                    title={getTemplateTitle()}
                    subtitle={getTemplateDescription()}
                    titleMetadata={templateId && templateId != "" && templateId != 'default' && (updateInprogress ? <Badge tone={'attention'}>{`${t("Product Update Inprogress")}`}</Badge> : productsAssigned && <Badge tone={'success'}>{`${productsAssigned ?? 0} ${t("product(s) assigned")}`}</Badge>)}
                >
                    <AttributeMapBanner className="custom-template__banner" />
                    {showContextualSaveBar && (process.env.REACT_APP_PROD === "0" ||
                        isAdminLoggedIn()) && (
                            <ContextualSaveBar
                                message={t("Unsaved changes")}
                                saveAction={{
                                    onAction: () => isAdminLoggedIn() ? askFortemplateValidation() : saveProducttemplate(),
                                    loading: saveloader,
                                    disabled: attributesLoader
                                }}
                                discardAction={{
                                    disabled: !templateId && isOpen,
                                    onAction: () => {
                                        setvalidationModal({
                                            ...validationModal,
                                            open: false
                                        })
                                        if (attributesLoader) {
                                            props.error(t('Kindly wait, some sections are loading!'));
                                        } else
                                            handleDiscardBtn()
                                    },
                                }}
                            />
                        )}
                    <Provider value={{
                        product_type: categoryAndAttributesSettings?.category_settings?.product_type?.[0] ?? '',
                        updateSingleWarehouseCondition,
                        filterAttributesLoader: filterAttributesLoader,
                        setFilterAttributesLoader: setFilterAttributesLoader,
                        resetInventorySettingsToDefault: resetInventorySettingsToDefault,
                        resetPriceSettingsToDefault: resetPriceSettingsToDefault,
                        handleContextualSaveBar: handleContextualSaveBar,
                        setBackendQuery: setBackendQuery,
                        setDisableBarcodeExemption: setDisableBarcodeExemption,
                        disableBarcodeExemption: disableBarcodeExemption,
                        wareHouseOptions: wareHouseOptions,
                        shopifyAttributes: shopifyAttributes,
                        data: "Template check",
                        onProductSettingsChange: onProductSettingsChange,
                        onInventorySettingsChange: onInventorySettingsChange,
                        onPriceSettingsChange: onPriceSettingsChange,
                        onFilterSettingsChange: onFilterSettingsChange,
                        onCategorySettingsChange: onCategorySettingsChange,
                        onAttributesSettingsChange: onAttributesSettingsChange,
                        errors: errors,
                        setErrors: setErrors,
                        setDefaultAttributes: setDefaultAttributes,
                        savedProfileData: JSON.parse(JSON.stringify(savedProfileData)),
                        templateId: templateId,
                        filterSettings: filterSettings,
                        setFilterSettings: setFilterSettings,
                        discardBtnClicked: discardBtnClicked,
                        setDiscardBtnClicked: setDiscardBtnClicked,
                        resetCat: resetCat,
                        deleteInContextualSaveBar: deleteInContextualSaveBar,
                        setSavedProfileData,
                        setOnClickResetFilters: setOnClickResetFilters,
                        setIsAnotherProfileProductExists: setIsAnotherProfileProductExists,
                        onClickResetFilters: onClickResetFilters,
                        productsAssigned: productsAssigned,
                        isClonedTemplate,
                        saveloader,
                        roundOffUi,
                        setRoundOffUi,
                        setPriceSettings,
                        contextualSaveBarData,
                        setContextualSaveBarData,
                        manualProductInAnotherTemplate,
                        setManualProductInAnotherTemplate,
                        JSONFeedAttributesData,
                        setJSONFeedDataAttributes,
                        renderJSONFeed,
                        setRenderJSONFeed,
                        setCurrentData,
                        savedJSONFeedAttributesData,
                        // hasActiveProducts
                        isNewTemplate,
                        setIsNewTemplate,
                        allProductTypes,
                        setInventorySettings,
                        showBusinessPrice,
                        setShowBusinessPrice,
                        savedShowBusinessPrice,
                        templateName
                    }}>
                        <SettingsTour templateName={templateName} categoryData={categoryAndAttributesSettings} inventorySettings={inventorySettings} JSONFeedRef={JSONFeedRef} filterSettings={filterSettings} contextualSaveBarData={contextualSaveBarData} jsonDataOnUi={jsonDataOnUI} selectedProductCount={selectedProductCount} templateId={templateId} />
                        {/* <Layout> */}
                        <BlockStack gap='400'>
                            <TopTabs categoryAndAttributesSettings={categoryAndAttributesSettings} isNewTemplate={isNewTemplate} attributesLoader={attributesLoader} shopifyAttributesLoader={shopifyAttributesLoader} />
                            {templateId != "default" && <Suspense fallback={<SmallSkeleton t={t} type="" />}>
                                <MemoizedTemplateName templateName={templateName} setTemplateName={setTemplateName} onProductSettingsChange={onProductSettingsChange} />
                            </Suspense>}

                            {templateId != "default" && <Suspense fallback={<FullfillmentSkeleton type="" t={t} />}>
                                <MemoizedFulfillmentType fulfillmentType={fulfillmentType} setFulfillmentType={setFulfillmentType} />
                            </Suspense>}

                            {templateId != "default" && <Suspense fallback={<CategorySkeleton t={t} type="" />}>
                                <MemoizedCategorySettingsAndAttributes
                                    data={categoryAndAttributesSettings}
                                    defaultAttributes={defaultAttributes}
                                    setattributesLoader={setattributesLoader}
                                    attributesLoader={attributesLoader}
                                    shopifyAttributesLoader={shopifyAttributesLoader}
                                />
                            </Suspense>}

                            {
                                !shopifyAttributesLoader && !attributesLoader && !!categoryAndAttributesSettings?.category_settings?.product_type?.[0] && JSONFeedAttributesData && !!categoryAndAttributesSettings?.category_settings?.primary_category &&
                                <Suspense fallback={jsonAttributesSkeleton()}>
                                    <div id='attributes_mapping' className='bulktour_template_attributes'>
                                        <JSONFeedAttributes
                                            key='template'
                                            ref={JSONFeedRef}
                                            from='template'
                                            getData={{ ...savedJSONFeedAttributesData }}
                                            parent_loading={attributesLoader}
                                            product_type={categoryAndAttributesSettings?.category_settings?.product_type?.[0] ?? ''}
                                            savedProductType={Array.isArray(savedProfileData?.category_id?.product_type) ? savedProfileData?.category_id?.product_type[0] : savedProfileData?.category_id?.product_type ?? ""}
                                            onChange={(change: boolean, data: JSONFeedBackendDataI) => {
                                                setContextualSaveBarData((a: any) => {
                                                    return { ...a, jsonFeed: change }
                                                })
                                                setJsonDataOnUi(data);
                                            }}
                                            additionalDataFromTemplateProd={{
                                                // variants: state?.variants ?? [],
                                                // products_value_on_shopify: state?.products_value_on_shopify ?? [],
                                                // shopify_data: state?.shopify_data ?? {},
                                                validatorErrAfterSave: validatorErrAfterSave,
                                                // upload_errors: extractAllErrors('amazon'),
                                                // upload_errors: [],
                                                // saved_errors: state?.shopify_data?.edited?.saved_errors ?? []
                                                // saved_errors: []
                                            }}
                                            currentData={JSON.parse(JSON.stringify(currentData))}
                                            product_attributes={JSON.parse(JSON.stringify(shopifyAttributes))}
                                            renderJSONFeed={renderJSONFeed}
                                            setRenderJSONFeed={setRenderJSONFeed}
                                            category_attributes={JSON.parse(JSON.stringify(JSONFeedAttributesData))}
                                            {...props}
                                        />
                                    </div>
                                </Suspense>
                            }


                            <Suspense fallback={<InventorySkeleton t={t} type="" />}>
                                <MemoizedInventorySettings inventorySettings={inventorySettings} />
                            </Suspense>

                            <Suspense fallback={<PriceSkeleton t={t} type="" />}>
                                <MemoizedPriceSettings priceSettings={priceSettings} />
                            </Suspense>

                            <Suspense fallback={<ProductSkeleton t={t} type="" />}>
                                <MemoizedProductSettings productSettings={productSettings} inventorySettings={inventorySettings} fulfillmentType={fulfillmentType} />
                            </Suspense>

                            {
                                templateId != "default" && <Suspense fallback={<FilterSkelton t={t} type="" />}>
                                    <FilterProducts filterSettings={filterSettings} setSelectedProductCount={setSelectedProductCount} />
                                </Suspense>
                            }
                            {(process.env.REACT_APP_PROD === "0" || isAdminLoggedIn()) && templateId && templateId != "default" && !filterAttributesLoader && <Suspense fallback={<><Layout.AnnotatedSection>Loading Import/Export Banner...</Layout.AnnotatedSection><br /></>}>
                                <MemoizedBulkAttributeEdit />
                            </Suspense>}
                        </BlockStack >
                        {/* </Layout> */}
                    </Provider >

                    <FooterHelp>
                        <InlineStack gap="100" blockAlign="center">
                            <div>
                                <Icon
                                    source={CircleInformationMajor}
                                    tone="info"
                                />
                            </div>
                            <div>Learn more about </div>
                            <Link target='_blank' url="https://docs.cedcommerce.com/shopify/multi-account-amazon-cedcommerce/?section=product-templates-2">
                                CedCommerce Amazon Channel,
                            </Link><Link target='_blank' url='https://amazon-sales-channel-app-backend.cifapps.com/Cedcommerce_Privacy_Statement.pdf'>
                                Terms & Conditions
                            </Link>and<Link target='_blank' url='https://amazon-sales-channel-app-backend.cifapps.com/Data_Privacy_Rights.pdf'>
                                Data Privacy Rights.
                            </Link> Need Help?<Link target='_blank'
                                url={`https://admin.shopify.com/apps${process.env.REACT_APP_SHOPIFY_APP_BASE}/panel/user/support`}>
                                Get Support
                            </Link>
                            <div> or </div>
                            <Link target='_blank' url="https://calendly.com/cedcommerce-awe-sales/sales" >
                                Book a Call
                            </Link>
                        </InlineStack>
                    </FooterHelp>
                </Page >
            </React.Fragment >
        );
    }
}
export default DI(Template);